import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { MemberPicker, type MemberCandidate } from "../components/MemberPicker.js";
import { AreaPicker, type AreaCandidate } from "../components/AreaPicker.js";
import { areaCandidates, type AdminSubjectArea } from "../lib/subjectAreaAccess.js";
import { SyncPolicyCard } from "../components/mirror/SyncPolicyCard.js";
import { invalidateMyGroups } from "../lib/scope.js";
import { reachLine, type EffectiveModel } from "../lib/reachLine.js";

interface DeliverabilityRow {
  account_id: string;
  display_name: string | null;
  address: string;
  reason: string;
  subtype: string | null;
  first_observed_at: string;
  last_observed_at: string;
  observation_count: number;
}

interface OffboardPreview {
  account: { account_id: string; display_name: string | null; email: string; kind: "user" | "contact" };
  non_accountable_items: number;
  accountable_items: Array<{ action_item_id: string; title: string; severity: string }>;
  group_memberships: Array<{ group_id: string; name: string }>;
  subscription_recipients: Array<{ subscription_id: string; name: string }>;
  proxy_for: Array<{ account_id: string; display_name: string | null }>;
}
import {
  Background,
  BaseEdge,
  Handle,
  MarkerType,
  Position,
  ReactFlow,
  getBezierPath,
  getSmoothStepPath,
  useConnection,
  type Connection,
  type Edge as FlowEdge,
  type EdgeProps,
  type Node as FlowNode,
  type NodeProps
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  BOX_WIDTH,
  USER_BOX_HEIGHT,
  computeOrgLayout,
  contactsBoxHeight,
  stackBoxHeight,
  type OrgBox,
  type OrgEdgeInput,
  type OrgPersonCell
} from "../lib/orgLayout.js";

type Tab = "users" | "groups" | "organization";
type TypeFilter = "all" | "user" | "contact";

type TenantEntitlement = "viewer" | "builder" | "admin";
type TenantMemberStatus = "invited" | "active" | "disabled" | "locked";

type TenantMemberRow = {
  account_id: string;
  email: string;
  display_name: string | null;
  account_type: "user" | "contact";
  entitlement: TenantEntitlement;
  status: TenantMemberStatus;
  proxy_account_id: string | null;
  proxy_display_name: string | null;
  proxy_email: string | null;
  escalates_to_account_id: string | null;
  escalates_to_display_name: string | null;
  /**
   * ADR-0160 — the agent role assigned to this person, decided here in advance.
   * Connecting an AI assistant materializes it; it never decides it. `null`
   * means no named assignment: they fall to the workspace's general access, or
   * cannot connect at all if there is none.
   */
  agent_role_id: string | null;
  agent_role_name: string | null;
  /** How many live connections a role change would re-scope (drives the confirm). */
  active_connection_count: number;
  /**
   * ADR-0212 §4.5 — the person's subject areas, by name. Recorded now; nothing
   * restricts anyone by them until access by subject area ships.
   */
  subject_areas: Array<{ id: string; name: string }>;
  /**
   * ADR-0213 — Query beyond the model: lets them query views outside their
   * subject areas; columns restricted for them stay restricted. Recorded now;
   * it takes effect when access by subject area ships.
   */
  query_beyond_model: boolean;
  /** ADR-0213 ruling 84 — Atlas Query, switched on for them by an admin. */
  atlas_query: boolean;
  invite: {
    invite_id: string;
    created_at: string;
    expires_at: string;
    consumed_at: string | null;
    revoked_at: string | null;
    send_count: number;
    state: "pending" | "expired" | "revoked" | "accepted";
  } | null;
};

type TenantUsersResponse = {
  tenant: { id: string; seat_limit: number | null };
  seats: { seat_used: number; active_users: number; invited_users: number };
  members: TenantMemberRow[];
};

/** ADR-0173 — one org-chart edge; display names ride for rendering (ids are wiring only). */
type OrgEdgeRow = {
  id: string;
  account_id: string;
  manager_account_id: string;
  kind: "solid" | "dotted";
  account_display_name: string;
  manager_display_name: string;
};

/** ADR-0160 — the tenant's agent roles, for the assignment select. Names only. */
type AgentRoleOption = { id: string; name: string };

/**
 * ADR-0160 §5.1 — the workspace's "everyone else" floor, read so the People page
 * can NAME it whenever it is active. The floor is never silent: an admin
 * scanning this page must be able to see what an unassigned person actually
 * gets, without going to another screen to find out.
 */
type ConnectionFloor = { policy: string; default_role_name: string | null };

type CreatePersonType = "user" | "contact";

type PeopleSortKey = "name" | "email";
type SortDir = "asc" | "desc";

function PeopleSortableTH(props: {
  col: PeopleSortKey;
  sortKey: PeopleSortKey;
  sortDir: SortDir;
  onClick: () => void;
  style?: React.CSSProperties;
  children: React.ReactNode;
}) {
  const active = props.sortKey === props.col;
  const arrow = active ? (props.sortDir === "asc" ? " ↑" : " ↓") : "";
  return (
    <th style={{ cursor: "pointer", userSelect: "none", ...props.style }} onClick={props.onClick}>
      {props.children}
      {arrow}
    </th>
  );
}

type GroupListRow = {
  id: string;
  name: string;
  status: "active" | "inactive";
  member_count: number;
  menu_count: number;
  created_at: string;
  updated_at: string;
};

type GroupMemberRow = { account_id: string; email: string; display_name: string | null; created_at: string };
type GroupDetail = { id: string; name: string; status: "active" | "inactive" };
type TenantUserPicker = { account_id: string; email: string; display_name: string | null };
type TenantMemberApi = TenantUserPicker & { status: string };

function formatTimestamp(dateStr: string): string {
  const date = new Date(dateStr);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = months[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const minutesStr = minutes < 10 ? `0${minutes}` : minutes;
  return `${month} ${day}, ${year} ${hours}:${minutesStr}${ampm}`;
}

function tabFromSearch(raw: string | null): Tab {
  return raw === "groups" ? "groups" : raw === "organization" ? "organization" : "users";
}

function typeFromSearch(raw: string | null): TypeFilter {
  return raw === "user" || raw === "contact" ? raw : "all";
}

function userLabel(u: { email: string; display_name: string | null }): string {
  return u.display_name ? `${u.display_name} (${u.email})` : u.email;
}

export function TenantAdminPeople() {
  const toast = useToast();
  const { confirm: confirmDialog } = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = tabFromSearch(searchParams.get("tab"));
  const typeFilter = typeFromSearch(searchParams.get("type"));
  const selectedGroupId = searchParams.get("group")?.trim() || "";

  const goToUsersTab = useCallback(() => {
    setSearchParams(
      (prev) => { const p = new URLSearchParams(prev); p.set("tab", "users"); p.delete("group"); return p; },
      { replace: true }
    );
  }, [setSearchParams]);

  const setType = useCallback((next: TypeFilter) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === "all") p.delete("type");
        else p.set("type", next);
        p.delete("person");
        return p;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const selectPerson = useCallback((accountId: string | null) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (accountId) p.set("person", accountId);
        else p.delete("person");
        return p;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const goToGroupsTab = useCallback(() => {
    setSearchParams(
      (prev) => { const p = new URLSearchParams(prev); p.set("tab", "groups"); return p; },
      { replace: true }
    );
  }, [setSearchParams]);

  const goToOrganizationTab = useCallback(() => {
    setSearchParams(
      (prev) => { const p = new URLSearchParams(prev); p.set("tab", "organization"); p.delete("group"); return p; },
      { replace: true }
    );
  }, [setSearchParams]);

  const selectGroup = useCallback((groupId: string | null) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("tab", "groups");
        if (groupId) p.set("group", groupId);
        else p.delete("group");
        return p;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  // ── Users tab ─────────────────────────────────────────────────────
  const [data, setData] = useState<TenantUsersResponse | null>(null);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [agentRoles, setAgentRoles] = useState<AgentRoleOption[]>([]);
  const [connectionFloor, setConnectionFloor] = useState<ConnectionFloor | null>(null);
  const [query, setQuery] = useState("");
  // Unified create modal (User | Contact). Spec §6.4.
  const [createOpen, setCreateOpen] = useState(false);
  const [createType, setCreateType] = useState<CreatePersonType>("user");
  const [createEmail, setCreateEmail] = useState("");
  const [createDisplayName, setCreateDisplayName] = useState("");
  const [createEntitlement, setCreateEntitlement] = useState<TenantEntitlement>("viewer");
  const [createProxyId, setCreateProxyId] = useState<string | null>(null);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<PeopleSortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  function togglePeopleSort(col: PeopleSortKey) {
    if (sortKey === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(col);
      setSortDir("asc");
    }
  }

  // Selected row detail (expanded card below table, like the groups pattern).
  const selectedAccountId = searchParams.get("person")?.trim() || "";
  const [detailBusy, setDetailBusy] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // ── Groups tab ────────────────────────────────────────────────────
  const [groupRows, setGroupRows] = useState<GroupListRow[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState<string | null>(null);
  const [createGroupModalOpen, setCreateGroupModalOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [creatingGroup, setCreatingGroup] = useState(false);

  // ── Group detail ──────────────────────────────────────────────────
  const [detailGroup, setDetailGroup] = useState<GroupDetail | null>(null);
  const [detailMembers, setDetailMembers] = useState<GroupMemberRow[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [tenantUsers, setTenantUsers] = useState<TenantUserPicker[]>([]);
  const [availableSelected, setAvailableSelected] = useState<string[]>([]);
  const [membersSelected, setMembersSelected] = useState<string[]>([]);
  const [memberOpsLoading, setMemberOpsLoading] = useState(false);

  // Reset selection state whenever the selected group changes
  const prevGroupIdRef = useRef("");
  if (prevGroupIdRef.current !== selectedGroupId) {
    prevGroupIdRef.current = selectedGroupId;
    if (availableSelected.length > 0) setAvailableSelected([]);
    if (membersSelected.length > 0) setMembersSelected([]);
  }

  // ADR-0203 — who the mail provider is refusing. Loaded beside the people
  // list and DEGRADED, never blocking: a failure here must not stop an admin
  // seeing their people.
  const [deliverability, setDeliverability] = useState<Map<string, DeliverabilityRow>>(new Map());
  const [offboard, setOffboard] = useState<{ row: TenantMemberRow; preview: OffboardPreview; successor: string | null } | null>(null);
  const [revoke, setRevoke] = useState<{ row: TenantMemberRow; preview: OffboardPreview; proxy: string | null; successor: string | null } | null>(null);
  const refreshDeliverability = useCallback(async () => {
    try {
      const r = await apiFetch<{ statuses: DeliverabilityRow[] }>("/api/tenant/admin/people/deliverability");
      setDeliverability(new Map(r.statuses.map((row) => [row.account_id, row])));
    } catch {
      setDeliverability(new Map());
    }
  }, []);

  const refreshUsers = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    if (!silent) setUsersLoading(true);
    setUsersError(null);
    try {
      const response = await apiFetch<TenantUsersResponse>("/api/tenant/admin/users");
      setData(response);
    } catch (e) {
      setUsersError(e instanceof Error ? e.message : String(e));
    } finally {
      if (!silent) setUsersLoading(false);
    }
  }, []);

  // ── ADR-0212 §4.5: the tenant's subject areas, for the detail panel's picker ──
  const [subjectAreas, setSubjectAreas] = useState<AdminSubjectArea[] | null>(null);
  const [subjectAreasError, setSubjectAreasError] = useState<string | null>(null);
  const loadSubjectAreas = useCallback(async () => {
    try {
      const res = await apiFetch<{ subject_areas: AdminSubjectArea[] }>("/api/tenant/admin/subject-areas");
      setSubjectAreas(res.subject_areas);
      setSubjectAreasError(null);
    } catch (e) {
      setSubjectAreasError(e instanceof Error ? e.message : String(e));
    }
  }, []);
  const subjectAreaCandidates: AreaCandidate[] = useMemo(() => areaCandidates(subjectAreas ?? []), [subjectAreas]);

  // ── ADR-0173: org edges (Organization tab, Reports-to column, detail fields) ──
  const [orgEdges, setOrgEdges] = useState<OrgEdgeRow[] | null>(null);
  const [orgError, setOrgError] = useState<string | null>(null);
  const loadOrgEdges = useCallback(async () => {
    try {
      const res = await apiFetch<{ edges: OrgEdgeRow[] }>("/api/tenant/admin/org-edges");
      setOrgEdges(res.edges);
      setOrgError(null);
    } catch (e) {
      setOrgError(e instanceof Error ? e.message : String(e));
    }
  }, []);
  useEffect(() => {
    void loadOrgEdges();
  }, [loadOrgEdges]);

  /** account_id → its solid edge; account_id → dotted edges (as targets). */
  const solidEdgeByAccount = useMemo(() => {
    const m = new Map<string, OrgEdgeRow>();
    for (const e of orgEdges ?? []) if (e.kind === "solid") m.set(e.account_id, e);
    return m;
  }, [orgEdges]);
  const dottedEdgesByAccount = useMemo(() => {
    const m = new Map<string, OrgEdgeRow[]>();
    for (const e of orgEdges ?? []) {
      if (e.kind !== "dotted") continue;
      const list = m.get(e.account_id);
      if (list) list.push(e);
      else m.set(e.account_id, [e]);
    }
    return m;
  }, [orgEdges]);

  // Org-edge writes. Autosave discipline: persist immediately, error toast
  // with the server's message VERBATIM (cycle refusals must surface as sent),
  // no success toast.
  const orgApiError = (e: unknown): string =>
    e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e);

  const createOrgEdge = useCallback(async (accountId: string, managerId: string, kind: "solid" | "dotted") => {
    await apiFetch<{ edge: OrgEdgeRow }>("/api/tenant/admin/org-edges", {
      method: "POST",
      body: { account_id: accountId, manager_account_id: managerId, kind }
    });
  }, []);
  const patchOrgEdgeKind = useCallback(async (edgeId: string, kind: "solid" | "dotted") => {
    await apiFetch<{ edge: OrgEdgeRow }>(`/api/tenant/admin/org-edges/${edgeId}`, {
      method: "PATCH",
      body: { kind }
    });
  }, []);
  const removeOrgEdge = useCallback(async (edgeId: string) => {
    await apiFetch<{ ok: true }>(`/api/tenant/admin/org-edges/${edgeId}`, { method: "DELETE" });
  }, []);

  /**
   * Detail-panel "Reports to" — upsert/remove the SOLID edge (spec §3.2).
   * Replacement runs cycle-safe: create the target edge as dotted first (the
   * hub validates the union graph with nothing yet changed), promote it
   * (atomic demote+promote), then remove the demoted old edge.
   */
  async function applySolidReparent(accountId: string, targetId: string) {
    const current = solidEdgeByAccount.get(accountId) ?? null;
    if ((current?.manager_account_id ?? null) === targetId) return;
    const existingPair = (orgEdges ?? []).find(
      (e) => e.account_id === accountId && e.manager_account_id === targetId
    );
    if (existingPair) {
      await patchOrgEdgeKind(existingPair.id, "solid");
      if (current && current.id !== existingPair.id) await removeOrgEdge(current.id);
    } else if (!current) {
      await createOrgEdge(accountId, targetId, "solid");
    } else {
      await createOrgEdge(accountId, targetId, "dotted");
      const fresh = await apiFetch<{ edges: OrgEdgeRow[] }>("/api/tenant/admin/org-edges");
      const created = fresh.edges.find(
        (e) => e.account_id === accountId && e.manager_account_id === targetId
      );
      if (created) {
        await patchOrgEdgeKind(created.id, "solid");
        await removeOrgEdge(current.id);
      }
    }
  }

  async function saveReportsTo(row: TenantMemberRow, targetId: string | null) {
    const current = solidEdgeByAccount.get(row.account_id) ?? null;
    if ((current?.manager_account_id ?? null) === targetId) return;
    setDetailBusy(true);
    try {
      if (targetId === null) {
        if (current) await removeOrgEdge(current.id);
      } else {
        await applySolidReparent(row.account_id, targetId);
      }
    } catch (e) {
      toast.push({ kind: "error", message: orgApiError(e) });
    } finally {
      setDetailBusy(false);
      void loadOrgEdges();
    }
  }

  /**
   * ADR-0212 §4.5 — the person's subject areas: each add or remove sends the
   * whole set, alone (never combined with other fields, so a refused set
   * leaves nothing half-applied), then re-reads — as dotted managers do. The
   * hub's refusal sentence is the error toast.
   */
  async function saveSubjectAreas(row: TenantMemberRow, nextAreaIds: string[]) {
    setDetailBusy(true);
    try {
      await apiFetch(`/api/tenant/admin/users/${row.account_id}`, { method: "PATCH", body: { subject_area_ids: nextAreaIds } });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setDetailBusy(false);
      await refreshUsers({ silent: true });
    }
  }

  /**
   * ADR-0213 — Query beyond the model, autosaved alone as the area picker saves
   * its set: the hub's refusal sentence is the error toast, no success toast,
   * then a silent re-read.
   */
  async function saveQueryBeyondModel(row: TenantMemberRow, value: boolean) {
    setDetailBusy(true);
    try {
      await apiFetch(`/api/tenant/admin/users/${row.account_id}`, { method: "PATCH", body: { query_beyond_model: value } });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setDetailBusy(false);
      await refreshUsers({ silent: true });
    }
  }

  /** ADR-0213 ruling 84 — Atlas Query, autosaved alone as Query beyond the model is. */
  async function saveAtlasQuery(row: TenantMemberRow, value: boolean) {
    setDetailBusy(true);
    try {
      await apiFetch(`/api/tenant/admin/users/${row.account_id}`, { method: "PATCH", body: { atlas_query: value } });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setDetailBusy(false);
      await refreshUsers({ silent: true });
    }
  }

  /** Detail-panel "Dotted-line managers" — diff-persist against current dotted edges. */
  async function saveDottedManagers(row: TenantMemberRow, nextManagerIds: string[]) {
    const current = dottedEdgesByAccount.get(row.account_id) ?? [];
    const currentIds = new Set(current.map((e) => e.manager_account_id));
    const nextIds = new Set(nextManagerIds);
    setDetailBusy(true);
    try {
      for (const e of current) {
        if (!nextIds.has(e.manager_account_id)) await removeOrgEdge(e.id);
      }
      for (const id of nextManagerIds) {
        if (!currentIds.has(id)) await createOrgEdge(row.account_id, id, "dotted");
      }
    } catch (e) {
      toast.push({ kind: "error", message: orgApiError(e) });
    } finally {
      setDetailBusy(false);
      void loadOrgEdges();
    }
  }

  const loadGroupList = useCallback(async () => {
    setGroupsLoading(true);
    setGroupsError(null);
    try {
      const res = await apiFetch<{ groups: GroupListRow[] }>("/api/tenant/admin/groups");
      setGroupRows(res.groups);
    } catch (e) {
      setGroupsError(e instanceof Error ? e.message : String(e));
    } finally {
      setGroupsLoading(false);
    }
  }, []);

  const loadGroupDetail = useCallback(async (groupId: string) => {
    if (!groupId) { setDetailGroup(null); setDetailMembers([]); return; }
    setDetailLoading(true);
    try {
      const res = await apiFetch<{ group: GroupDetail; members: GroupMemberRow[] }>(
        `/api/tenant/admin/groups/${encodeURIComponent(groupId)}`
      );
      setDetailGroup(res.group);
      setDetailMembers(res.members);
    } catch {
      setDetailGroup(null);
      setDetailMembers([]);
      toast.push({ kind: "error", message: "Could not load team." });
    } finally {
      setDetailLoading(false);
    }
  }, [toast]);

  /**
   * ADR-0160 §5.1 — the agent roles offered in the assignment select, and the
   * workspace floor named in the help line. Both are read-only here; the roles
   * themselves and the floor are managed in Settings. Failure is non-fatal: the
   * people table stays usable, the select just has nothing to offer.
   */
  const loadAgentRoleContext = useCallback(async () => {
    try {
      const [rolesRes, connectionsRes] = await Promise.all([
        apiFetch<{ agent_roles: AgentRoleOption[] }>("/api/tenant/admin/agent-roles"),
        apiFetch<{ user_connections: ConnectionFloor }>("/api/tenant/admin/user-connections"),
      ]);
      setAgentRoles(
        [...rolesRes.agent_roles].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
      );
      setConnectionFloor(connectionsRes.user_connections);
    } catch { /* non-fatal — the table still works without the assignment column's options */ }
  }, []);

  const loadTenantUsers = useCallback(async () => {
    try {
      const res = await apiFetch<{ members: TenantMemberApi[] }>("/api/tenant/admin/users");
      setTenantUsers(
        res.members
          .filter((m) => m.status === "active")
          .map((m) => ({ account_id: m.account_id, email: m.email, display_name: m.display_name }))
      );
    } catch { /* optional */ }
  }, []);

  useEffect(() => {
    void refreshUsers();
    void refreshDeliverability();
    void loadAgentRoleContext();
    void loadSubjectAreas();
    let last = 0;
    function maybeRefresh() {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - last < 15_000) return;
      last = now;
      void refreshUsers({ silent: true });
    }
    window.addEventListener("focus", maybeRefresh);
    document.addEventListener("visibilitychange", maybeRefresh);
    return () => {
      window.removeEventListener("focus", maybeRefresh);
      document.removeEventListener("visibilitychange", maybeRefresh);
    };
  }, [refreshUsers, loadAgentRoleContext]);

  useEffect(() => {
    if (tab === "groups") {
      void loadGroupList();
      void loadTenantUsers();
    }
  }, [tab, loadGroupList, loadTenantUsers]);

  useEffect(() => {
    if (tab === "groups" && selectedGroupId) void loadGroupDetail(selectedGroupId);
    if (tab === "groups" && !selectedGroupId) { setDetailGroup(null); setDetailMembers([]); }
  }, [tab, selectedGroupId, loadGroupDetail]);

  // ── Users actions ─────────────────────────────────────────────────
  function resetCreateForm() {
    setCreateEmail("");
    setCreateDisplayName("");
    setCreateEntitlement("viewer");
    setCreateProxyId(null);
    setCreateError(null);
  }

  async function submitCreate() {
    setCreateSaving(true);
    setCreateError(null);
    try {
      if (createType === "user") {
        await apiFetch("/api/tenant/admin/users/invite", {
          method: "POST",
          body: {
            email: createEmail.trim(),
            entitlement: createEntitlement,
            ...(createDisplayName.trim() ? { display_name: createDisplayName.trim() } : {})
          }
        });
        setCreateOpen(false);
        resetCreateForm();
        await refreshUsers();
      } else {
        const r = await apiFetch<{ contact: { account_id: string } }>("/api/tenant/admin/contacts", {
          method: "POST",
          body: {
            email: createEmail.trim(),
            display_name: createDisplayName.trim(),
            proxy_account_id: createProxyId ?? null
          }
        });
        setCreateOpen(false);
        resetCreateForm();
        await refreshUsers();
        selectPerson(r.contact.account_id);
      }
    } catch (e) {
      setCreateError(e instanceof ApiRequestError ? e.message : (e instanceof Error ? e.message : String(e)));
    } finally {
      setCreateSaving(false);
    }
  }

  async function resendInvite(inviteId: string) {
    setUsersLoading(true);
    setUsersError(null);
    try {
      await apiFetch(`/api/tenant/admin/users/invites/${inviteId}/resend`, { method: "POST" });
      await refreshUsers();
    } catch (e) {
      setUsersError(e instanceof Error ? e.message : String(e));
    } finally {
      setUsersLoading(false);
    }
  }

  async function updateEntitlement(accountId: string, entitlement: TenantEntitlement) {
    setUsersError(null);
    try {
      await apiFetch(`/api/tenant/admin/users/${accountId}`, { method: "PATCH", body: { entitlement } });
      await refreshUsers({ silent: true });
    } catch (e) {
      setUsersError(e instanceof Error ? e.message : String(e));
    }
  }

  /**
   * ADR-0160 §4.2 — assign, change, or clear a person's agent role.
   *
   * The change is IMMEDIATE, not next-connect: an admin re-scoping someone
   * expects it to be true now, and a live credential holding authority the
   * admin just removed is the posture this whole ADR exists to end. So when the
   * person has live connections we say exactly what will happen and ask first;
   * when they have none there is nothing to disrupt and it autosaves.
   */
  async function updateAgentRole(member: TenantMemberRow, nextRoleId: string) {
    const next = nextRoleId ? agentRoles.find((r) => r.id === nextRoleId) ?? null : null;
    const live = member.active_connection_count;
    const who = member.display_name?.trim() || member.email;
    const floorName = floorIsActive ? connectionFloor?.default_role_name ?? null : null;

    if (live > 0) {
      const connections = `${live} live connection${live === 1 ? "" : "s"}`;
      const detail = next
        ? `${who} will hold the ${next.name} role. Their ${connections} are re-scoped now and their AI assistant sessions disconnect, so tools reload with the new access.`
        : floorName
          ? `${who} will fall back to this workspace's general access: ${floorName}. Their ${connections} are re-scoped now and their AI assistant sessions disconnect.`
          : `${who} will have no connection role. Their ${connections} are disconnected and cannot reconnect until you assign them a role.`;
      const ok = await confirmDialog({
        title: next ? `Change agent role to ${next.name}?` : "Remove agent role?",
        detail,
        confirmLabel: next ? "Change role" : floorName ? "Remove role" : "Remove and disconnect",
      });
      if (!ok) return;
    }

    setUsersError(null);
    try {
      await apiFetch(`/api/tenant/admin/users/${member.account_id}`, {
        method: "PATCH",
        body: { agent_role_id: next?.id ?? null },
      });
      await refreshUsers({ silent: true });
    } catch (e) {
      setUsersError(e instanceof Error ? e.message : String(e));
      // Re-read so the select snaps back to the truth rather than showing a
      // value the server refused.
      await refreshUsers({ silent: true });
    }
  }

  async function updateUserStatus(accountId: string, status: "active" | "disabled") {
    setUsersError(null);
    try {
      await apiFetch(`/api/tenant/admin/users/${accountId}`, { method: "PATCH", body: { status } });
      await refreshUsers({ silent: true });
    } catch (e) {
      setUsersError(e instanceof Error ? e.message : String(e));
    }
  }

  async function removeTenantMember(accountId: string) {
    // ADR-0152 Stage 3: this used to warn that an admin's agent keys survive
    // their creator and need separate revoking. That is no longer true in
    // either direction — keys are retired, and a person's connections are
    // re-checked against their membership on every request, so removal ends
    // them immediately. The note now states the reassuring fact rather than a
    // chore that no longer exists.
    if (
      !window.confirm(
        "Remove this member? This action cannot be undone. Any AI tools they connected stop working immediately."
      )
    )
      return;
    setUsersLoading(true);
    setUsersError(null);
    try {
      await apiFetch(`/api/tenant/admin/users/${accountId}`, { method: "DELETE" });
      await refreshUsers();
    } catch (e) {
      setUsersError(e instanceof Error ? e.message : String(e));
    } finally {
      setUsersLoading(false);
    }
  }

  // ── Groups actions ────────────────────────────────────────────────
  async function createGroup() {
    const name = newGroupName.trim();
    if (!name) return;
    setCreatingGroup(true);
    try {
      const res = await apiFetch<{ group: { id: string } }>("/api/tenant/admin/groups", {
        method: "POST",
        body: { name }
      });
      setNewGroupName("");
      setCreateGroupModalOpen(false);
      await loadGroupList();
      selectGroup(res.group.id);
      invalidateMyGroups(); // ADR-0068: refresh scope-picker team list
      toast.push({ kind: "success", message: "Team created." });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes("group_name_taken") || msg.includes("409")) {
        toast.push({ kind: "error", message: "A team with that name already exists." });
      } else {
        toast.push({ kind: "error", message: msg });
      }
    } finally {
      setCreatingGroup(false);
    }
  }

  async function patchGroup(groupId: string, patch: { name?: string; status?: "active" | "inactive" }) {
    try {
      const res = await apiFetch<{ group: GroupDetail }>(
        `/api/tenant/admin/groups/${encodeURIComponent(groupId)}`,
        { method: "PATCH", body: patch }
      );
      if (selectedGroupId === groupId) setDetailGroup(res.group);
      await loadGroupList();
      invalidateMyGroups(); // ADR-0068: name or status change refreshes the picker
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  async function deleteGroup(groupId: string) {
    const ok = await confirmDialog({
      title: "Delete team?",
      detail: "Members will lose access to team-restricted menus.",
      confirmLabel: "Delete"
    });
    if (!ok) return;
    try {
      await apiFetch(`/api/tenant/admin/groups/${encodeURIComponent(groupId)}`, { method: "DELETE" });
      if (selectedGroupId === groupId) selectGroup(null);
      await loadGroupList();
      invalidateMyGroups(); // ADR-0068: removed team must leave the picker
      toast.push({ kind: "success", message: "Team deleted." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  async function addMembersToGroup() {
    if (!selectedGroupId || availableSelected.length === 0) return;
    setMemberOpsLoading(true);
    try {
      await Promise.all(
        availableSelected.map((accountId) =>
          apiFetch(`/api/tenant/admin/groups/${encodeURIComponent(selectedGroupId)}/members`, {
            method: "POST",
            body: { account_id: accountId }
          })
        )
      );
      setAvailableSelected([]);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setMemberOpsLoading(false);
      void loadGroupDetail(selectedGroupId);
      void loadGroupList();
      invalidateMyGroups(); // ADR-0068: adding self to team changes my-groups
    }
  }

  async function removeMembersFromGroup() {
    if (!selectedGroupId || membersSelected.length === 0) return;
    setMemberOpsLoading(true);
    try {
      await Promise.all(
        membersSelected.map((accountId) =>
          apiFetch(
            `/api/tenant/admin/groups/${encodeURIComponent(selectedGroupId)}/members/${encodeURIComponent(accountId)}`,
            { method: "DELETE" }
          )
        )
      );
      setMembersSelected([]);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setMemberOpsLoading(false);
      void loadGroupDetail(selectedGroupId);
      void loadGroupList();
      invalidateMyGroups(); // ADR-0068: removing self from team changes my-groups
    }
  }

  const selectedRow: TenantMemberRow | null = useMemo(() => {
    if (!selectedAccountId || !data) return null;
    return data.members.find((m) => m.account_id === selectedAccountId) ?? null;
  }, [selectedAccountId, data]);

  async function saveUserEscalatesTo(row: TenantMemberRow, target: string | null) {
    setDetailBusy(true);
    setDetailError(null);
    try {
      await apiFetch(`/api/tenant/admin/users/${row.account_id}`, {
        method: "PATCH",
        body: { escalates_to_account_id: target }
      });
      await refreshUsers({ silent: true });
      toast.push({ kind: "success", message: "Saved." });
    } catch (e) {
      if (e instanceof ApiRequestError) {
        setDetailError(e.code === "escalation_cycle"
          ? "That target would form a cycle in the escalation chain."
          : e.message);
      } else {
        setDetailError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setDetailBusy(false);
    }
  }

  async function saveUserDetail(row: TenantMemberRow, patch: { display_name?: string | null; escalates_to_account_id?: string | null }) {
    setDetailBusy(true);
    setDetailError(null);
    try {
      await apiFetch(`/api/tenant/admin/users/${row.account_id}`, { method: "PATCH", body: patch });
      await refreshUsers({ silent: true });
      toast.push({ kind: "success", message: "Saved." });
    } catch (e) {
      setDetailError(e instanceof ApiRequestError ? e.message : (e instanceof Error ? e.message : String(e)));
    } finally {
      setDetailBusy(false);
    }
  }

  async function resetUserPassword(row: TenantMemberRow) {
    const ok = await confirmDialog({
      title: "Send password reset?",
      detail: `A reset link will be emailed to ${row.email}. The link expires in 1 hour.`,
      confirmLabel: "Send reset email"
    });
    if (!ok) return;
    setDetailBusy(true);
    setDetailError(null);
    try {
      await apiFetch(`/api/tenant/admin/users/${row.account_id}/reset-password`, { method: "POST" });
      toast.push({ kind: "success", message: "Password reset email sent." });
    } catch (e) {
      setDetailError(e instanceof ApiRequestError ? e.message : (e instanceof Error ? e.message : String(e)));
    } finally {
      setDetailBusy(false);
    }
  }

  async function saveContactDetail(row: TenantMemberRow, patch: { display_name?: string; email?: string; proxy_account_id?: string | null }) {
    setDetailBusy(true);
    setDetailError(null);
    try {
      await apiFetch(`/api/tenant/admin/contacts/${row.account_id}`, { method: "PATCH", body: patch });
      await refreshUsers({ silent: true });
      toast.push({ kind: "success", message: "Saved." });
    } catch (e) {
      setDetailError(e instanceof ApiRequestError ? e.message : (e instanceof Error ? e.message : String(e)));
    } finally {
      setDetailBusy(false);
    }
  }

  // Promoting mints an INVITATION and changes nothing else — the contact stays
  // a contact, with their proxy and their email, until they accept. The flip to
  // user happens on acceptance, which is also why the row keeps a real
  // membership the whole time (revoking an invite deletes an 'invited' one).
  async function promoteContact(row: TenantMemberRow, entitlement: TenantEntitlement) {
    const who = row.display_name ?? row.email;
    const ok = await confirmDialog({
      title: `Invite ${who} to become a user?`,
      detail:
        `They will get an email with a link to set up a sign-in as a ${entitlement}. ` +
        `Nothing changes until they accept it — they stay a contact, keep their proxy, and keep receiving ` +
        `Atlas email exactly as they do now. Accepting is what makes them a user and releases their proxy.`,
      confirmLabel: "Send invite"
    });
    if (!ok) return;
    setDetailBusy(true);
    setDetailError(null);
    try {
      const r = await apiFetch<{ invite_email_sent: boolean }>(
        `/api/tenant/admin/contacts/${row.account_id}/promote`,
        { method: "POST", body: { entitlement } }
      );
      await refreshUsers({ silent: true });
      if (r.invite_email_sent) {
        toast.push({ kind: "success", message: `Invitation sent to ${who}. They stay a contact until they accept.` });
      } else {
        toast.push({
          kind: "info",
          message: `Invitation created for ${who}, but the email did not send. Use Resend on their row to try again.`
        });
      }
    } catch (e) {
      setDetailError(e instanceof ApiRequestError ? e.message : (e instanceof Error ? e.message : String(e)));
    } finally {
      setDetailBusy(false);
    }
  }

  async function clearDeliverability(row: TenantMemberRow) {
    const ok = await confirmDialog({
      title: "Clear the delivery block?",
      detail:
        "Atlas will start sending to this address again. If the mail provider is still holding it, delivery will keep failing until it is removed there too.",
      confirmLabel: "Clear"
    });
    if (!ok) return;
    setDetailBusy(true);
    try {
      await apiFetch(`/api/tenant/admin/people/deliverability/${row.account_id}`, { method: "DELETE" });
      await refreshDeliverability();
      toast.push({ kind: "success", message: "Delivery block cleared." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setDetailBusy(false);
    }
  }

  async function openOffboard(row: TenantMemberRow) {
    setDetailBusy(true);
    try {
      const p = await apiFetch<OffboardPreview>(`/api/tenant/admin/people/${row.account_id}/offboard-preview`);
      setOffboard({ row, preview: p, successor: null });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setDetailBusy(false);
    }
  }

  async function submitOffboard(finalState: "disabled" | "deleted") {
    if (!offboard) return;
    setDetailBusy(true);
    try {
      await apiFetch(`/api/tenant/admin/people/${offboard.row.account_id}/offboard`, {
        method: "POST",
        body: { accountable_reassign_to: offboard.successor, final_state: finalState }
      });
      setOffboard(null);
      selectPerson(null);
      await refreshUsers();
      await refreshDeliverability();
      toast.push({ kind: "success", message: finalState === "deleted" ? "Contact offboarded and deleted." : "Person offboarded." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setDetailBusy(false);
    }
  }

  // Revoking access reuses the offboard PREVIEW: despite the name, that
  // endpoint answers "what is this person entangled in?", which is the same
  // question both gestures have to ask. What they do with the answer is
  // opposite — offboarding strips the entanglements, this keeps every one of
  // them and takes the login instead.
  async function openRevokeAccess(row: TenantMemberRow) {
    setDetailBusy(true);
    try {
      const p = await apiFetch<OffboardPreview>(`/api/tenant/admin/people/${row.account_id}/offboard-preview`);
      setRevoke({ row, preview: p, proxy: null, successor: null });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setDetailBusy(false);
    }
  }

  async function submitRevokeAccess() {
    if (!revoke || !revoke.proxy) return;
    const who = revoke.row.display_name ?? revoke.row.email;
    setDetailBusy(true);
    try {
      const r = await apiFetch<{ revoked_connections: number; paused_subscriptions: number }>(
        `/api/tenant/admin/users/${revoke.row.account_id}/demote`,
        { method: "POST", body: { proxy_account_id: revoke.proxy, accountable_reassign_to: revoke.successor } }
      );
      setRevoke(null);
      await refreshUsers();
      // The connection and subscription counts are consequences the admin did
      // not ask for by name, so they are said out loud rather than left to be
      // discovered.
      const extra = [
        r.revoked_connections > 0 ? `${r.revoked_connections} assistant connection${r.revoked_connections === 1 ? "" : "s"} revoked` : null,
        r.paused_subscriptions > 0 ? `${r.paused_subscriptions} subscription${r.paused_subscriptions === 1 ? "" : "s"} they owned paused` : null
      ].filter(Boolean).join(", ");
      toast.push({
        kind: "success",
        message: `${who} is now a contact — they keep their action item roles and report subscriptions, and can no longer sign in.${extra ? ` (${extra}.)` : ""}`
      });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setDetailBusy(false);
    }
  }

  async function deleteContactRow(row: TenantMemberRow) {
    const ok = await confirmDialog({
      title: "Delete contact?",
      detail: "The contact will be removed from this tenant.",
      confirmLabel: "Delete"
    });
    if (!ok) return;
    setDetailBusy(true);
    try {
      await apiFetch(`/api/tenant/admin/contacts/${row.account_id}`, { method: "DELETE" });
      selectPerson(null);
      await refreshUsers();
      toast.push({ kind: "success", message: "Contact deleted." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setDetailBusy(false);
    }
  }

  const memberCandidates: MemberCandidate[] = useMemo(() => {
    if (!data) return [];
    return data.members
      .filter((m) => m.status === "active")
      .map((m) => ({
        id: m.account_id,
        kind: m.account_type,
        display_name: m.display_name ?? m.email,
        email: m.email
      }));
  }, [data]);

  const userOnlyCandidates: MemberCandidate[] = useMemo(
    () => memberCandidates.filter((c) => c.kind === "user"),
    [memberCandidates]
  );

  // ── Derived ───────────────────────────────────────────────────────
  const filteredMembers = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    const rows = data.members
      .filter((m) => typeFilter === "all" || m.account_type === typeFilter)
      .filter((m) => {
        if (!q) return true;
        return (
          m.email.toLowerCase().includes(q) ||
          m.display_name?.toLowerCase().includes(q) ||
          m.entitlement.toLowerCase().includes(q) ||
          // ADR-0160 §5.1 — searching a role name is how an admin answers
          // "who holds Operations Engineer?", the review this page exists for.
          m.agent_role_name?.toLowerCase().includes(q) ||
          // ADR-0212 §4.5 — "who holds Accounting?" is answered the same way.
          (m.subject_areas ?? []).some((a) => a.name.toLowerCase().includes(q)) ||
          m.status.toLowerCase().includes(q)
        );
      });
    const sorted = [...rows].sort((a, b) => {
      let cmp = 0;
      if (sortKey === "name") {
        const an = a.display_name ?? "";
        const bn = b.display_name ?? "";
        // Rows without a display name sort to the end regardless of direction.
        if (an === "" && bn !== "") return 1;
        if (an !== "" && bn === "") return -1;
        cmp = an.localeCompare(bn, undefined, { sensitivity: "base" });
        if (cmp === 0) cmp = a.email.localeCompare(b.email, undefined, { sensitivity: "base" });
      } else {
        cmp = a.email.localeCompare(b.email, undefined, { sensitivity: "base" });
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [data, query, typeFilter, sortKey, sortDir]);

  /**
   * ADR-0160 §4 — the floor only exists when the policy admits someone AND a
   * role is set. `off` means "no general access", so an unassigned person gets
   * nothing. Both halves matter: a policy that is on with no role reaches
   * nobody either.
   */
  const floorIsActive = connectionFloor !== null && connectionFloor.policy !== "off" && !!connectionFloor.default_role_name;

  const seatsLabel = data?.tenant.seat_limit
    ? `Seats: ${data.seats.seat_used} / ${data.tenant.seat_limit}`
    : `Seats: ${data?.seats.seat_used ?? 0} used (unlimited)`;

  const memberIds = new Set(detailMembers.map((m) => m.account_id));

  return (
    <div>
      <PageLayout title="Users & Teams">
        <div className="tabs">
          <button type="button" className={`tab${tab === "users" ? " tabActive" : ""}`} onClick={goToUsersTab}>
            Users
          </button>
          <button type="button" className={`tab${tab === "groups" ? " tabActive" : ""}`} onClick={goToGroupsTab}>
            Teams
          </button>
          <button type="button" className={`tab${tab === "organization" ? " tabActive" : ""}`} onClick={goToOrganizationTab}>
            Organization
          </button>
        </div>

        {/* ── Users tab ── */}
        {tab === "users" && (
          <div className="tabPanel">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 16, marginBottom: 12 }}>
              <span className="muted" style={{ fontSize: 13 }}>{seatsLabel}</span>
              <button className="btn btnPrimary" type="button" onClick={() => { resetCreateForm(); setCreateType("user"); setCreateOpen(true); }}>
                Add person
              </button>
            </div>

            {usersError && <ErrorState detail={usersError} />}
            {usersLoading && <LoadingState />}

            {!usersLoading && !usersError && data && (
              <>
                <div className="card" style={{ marginBottom: 12 }}>
                  <div className="toolbar">
                    <div className="toolbarGroup" style={{ flex: "1 1 380px" }}>
                      <input
                        className="input"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search by name, email, entitlement or status…"
                        aria-label="Search people"
                      />
                    </div>
                    <div className="toolbarGroup" role="group" aria-label="Type filter" style={{ display: "flex", gap: 4 }}>
                      <button
                        type="button"
                        className={`pill ${typeFilter === "all" ? "pillOk" : "pillNeutral"}`}
                        style={{ cursor: "pointer", border: 0 }}
                        onClick={() => setType("all")}
                      >All</button>
                      <button
                        type="button"
                        className={`pill ${typeFilter === "user" ? "pillOk" : "pillNeutral"}`}
                        style={{ cursor: "pointer", border: 0 }}
                        onClick={() => setType("user")}
                      >Users</button>
                      <button
                        type="button"
                        className={`pill ${typeFilter === "contact" ? "pillOk" : "pillNeutral"}`}
                        style={{ cursor: "pointer", border: 0 }}
                        onClick={() => setType("contact")}
                      >Contacts</button>
                    </div>
                  </div>
                  <div className="help" style={{ marginTop: 8 }}>
                    Showing <b>{filteredMembers.length}</b> of <b>{data.members.length}</b> people.
                  </div>
                </div>

                <div className="card tableWrap">
                  {data.members.length === 0 ? (
                    <div className="adminListEmpty">No users or contacts yet.</div>
                  ) : (
                    <table className="table">
                      <thead>
                        <tr>
                          <PeopleSortableTH col="name" sortKey={sortKey} sortDir={sortDir} onClick={() => togglePeopleSort("name")}>
                            Name
                          </PeopleSortableTH>
                          <PeopleSortableTH col="email" sortKey={sortKey} sortDir={sortDir} onClick={() => togglePeopleSort("email")}>
                            Email
                          </PeopleSortableTH>
                          <th style={{ textAlign: "center" }}>Type</th>
                          <th style={{ textAlign: "center" }}>Entitlement</th>
                          <th style={{ textAlign: "center" }}>Agent role</th>
                          <th>Subject areas</th>
                          <th style={{ textAlign: "center" }}>Query beyond the model</th>
                          <th style={{ textAlign: "center" }}>Status</th>
                          <th>Proxy</th>
                          <th>Reports to</th>
                          <th>Escalates to</th>
                          <th>Invited</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredMembers.map((member) => {
                          const isContact = member.account_type === "contact";
                          // A contact who has been invited to become a user keeps their
                          // membership 'active' — the flip happens when they accept — so the
                          // pending state has to come off the invite, not off the status.
                          const invitePending =
                            !member.invite?.consumed_at &&
                            (member.invite?.state === "pending" || member.invite?.state === "expired");
                          const isInvited = (!isContact && member.status === "invited") || (isContact && invitePending);
                          const isExpired = member.invite?.state === "expired";
                          const isActive = member.status === "active";
                          const isSelected = selectedAccountId === member.account_id;
                          return (
                            <tr
                              key={member.account_id}
                              style={{ background: isSelected ? "var(--surface-2, rgba(0,0,0,0.04))" : undefined, cursor: "pointer" }}
                              onClick={() => selectPerson(isSelected ? null : member.account_id)}
                            >
                              <td>
                                {member.display_name
                                  ? <div style={{ fontWeight: 600, fontSize: 14 }}>{member.display_name}</div>
                                  : <span className="muted">—</span>}
                              </td>
                              <td style={{ fontSize: 13 }}>{member.email}</td>
                              <td style={{ textAlign: "center" }}>
                                <span className={isContact ? "pill pillNeutral" : "pill"}>{isContact ? "Contact" : "User"}</span>
                              </td>
                              <td style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                                {isContact ? (
                                  <span className="muted">—</span>
                                ) : (
                                  <select
                                    className="pillSelect pillSelectNeutral"
                                    value={member.entitlement}
                                    onChange={(e) => void updateEntitlement(member.account_id, e.target.value as TenantEntitlement)}
                                    disabled={usersLoading}
                                    aria-label={`Entitlement for ${member.email}`}
                                  >
                                    <option value="viewer">viewer</option>
                                    <option value="builder">builder</option>
                                    <option value="admin">admin</option>
                                  </select>
                                )}
                              </td>
                              {/*
                                ADR-0160 §5.1 — the assignment home. Sits beside
                                Entitlement because that is literally where the
                                ruling put it: the same place a person's access
                                is decided. One datum per column, no pill beside
                                the select — "None" is a value, not a status.
                              */}
                              <td style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                                {isContact ? (
                                  <span className="muted">—</span>
                                ) : (
                                  <select
                                    className="pillSelect pillSelectNeutral"
                                    value={member.agent_role_id ?? ""}
                                    onChange={(e) => void updateAgentRole(member, e.target.value)}
                                    disabled={usersLoading || agentRoles.length === 0}
                                    aria-label={`Agent role for ${member.email}`}
                                  >
                                    <option value="">None</option>
                                    {agentRoles.map((r) => (
                                      <option key={r.id} value={r.id}>{r.name}</option>
                                    ))}
                                  </select>
                                )}
                              </td>
                              <td style={{ fontSize: 13 }}>
                                {isContact ? (
                                  <span className="muted">—</span>
                                ) : (member.subject_areas ?? []).length > 0 ? (
                                  member.subject_areas.map((a) => a.name).join(", ")
                                ) : (
                                  <span className="muted">None</span>
                                )}
                              </td>
                              <td style={{ textAlign: "center", fontSize: 13 }}>
                                {isContact ? <span className="muted">—</span> : member.query_beyond_model ? "Yes" : null}
                              </td>
                              <td style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                                {/* Beside the status, never instead of it: their
                                    account is fine, the mail is what is broken. */}
                                {deliverability.has(member.account_id) && (
                                  <span className="pill pillBad" style={{ marginRight: 6 }}>Undeliverable</span>
                                )}
                                {isInvited && !isExpired && <span className="pill">Invited</span>}
                                {isInvited && isExpired && <span className="pill pillBad">Expired</span>}
                                {/* Contacts get the same control as users. It used to be
                                    withheld from them because status only ever gated
                                    signing in, which a contact cannot do — so it read as
                                    meaningless. It isn't any more: status now decides
                                    whether Atlas mails them, and mail is the only thing a
                                    contact does. Disabling one has to be possible. */}
                                {!isInvited && (isActive || member.status === "disabled") && (
                                  <select
                                    className={isActive ? "pillSelect pillSelectOk" : "pillSelect pillSelectBad"}
                                    value={member.status}
                                    onChange={(e) => void updateUserStatus(member.account_id, e.target.value as "active" | "disabled")}
                                    disabled={usersLoading}
                                    aria-label={`Status for ${member.email}`}
                                  >
                                    <option value="active">Active</option>
                                    <option value="disabled">Disabled</option>
                                  </select>
                                )}
                              </td>
                              <td style={{ fontSize: 13 }}>
                                {isContact
                                  ? (member.proxy_display_name ?? <span className="muted">—</span>)
                                  : <span className="muted">—</span>}
                              </td>
                              <td style={{ fontSize: 13 }}>
                                {solidEdgeByAccount.get(member.account_id)?.manager_display_name ?? <span className="muted">—</span>}
                              </td>
                              <td style={{ fontSize: 13 }}>
                                {member.escalates_to_display_name ?? <span className="muted">—</span>}
                              </td>
                              <td style={{ fontSize: 13, color: "var(--muted)" }}>
                                {isContact && !isInvited
                                  ? <span className="muted">—</span>
                                  : (member.invite && (member.invite.consumed_at
                                      ? <>Accepted {formatTimestamp(member.invite.consumed_at)}</>
                                      : <>
                                          Invited {formatTimestamp(member.invite.created_at)}
                                          {member.invite.send_count > 1 && (
                                            <span style={{ marginLeft: 4, fontSize: 11, fontWeight: 600 }}>
                                              ×{member.invite.send_count}
                                            </span>
                                          )}
                                        </>))}
                              </td>
                              <td onClick={(e) => e.stopPropagation()}>
                                <div className="tenantUserActionBar">
                                  {isInvited && (
                                    <>
                                      <button
                                        type="button"
                                        className="tenantUserActionLink"
                                        onClick={() => { if (member.invite && !usersLoading) void resendInvite(member.invite.invite_id); }}
                                        disabled={usersLoading}
                                      >
                                        Resend
                                      </button>
                                      <span className="tenantUserActionSep" aria-hidden="true">·</span>
                                    </>
                                  )}
                                  <button
                                    type="button"
                                    className="tenantUserActionLink tenantUserActionDanger"
                                    onClick={() => {
                                      if (usersLoading) return;
                                      if (isContact) void deleteContactRow(member);
                                      else void removeTenantMember(member.account_id);
                                    }}
                                    disabled={usersLoading}
                                  >
                                    {isContact ? "Delete contact" : "Delete user"}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {filteredMembers.length === 0 && (
                          <tr>
                            <td colSpan={13} className="muted" style={{ padding: 12 }}>
                              No people match your filters.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>

                {/*
                  ADR-0160 §5.1 — the floor is never silent. Whatever an
                  unassigned person gets, this page says it, because an admin
                  scanning a column full of "None" must not have to guess
                  whether that means "no access" or "whatever the default is".
                  One line, one tenant-level fact — not per-row decoration.
                */}
                <div className="help" style={{ marginTop: 8 }}>
                  {floorIsActive ? (
                    <>
                      People with no agent role connect with this workspace's general access:{" "}
                      <b>{connectionFloor?.default_role_name}</b>. Change it in{" "}
                      <Link to="/app/admin/settings">Settings</Link>.
                    </>
                  ) : (
                    <>
                      People with no agent role can't connect an AI assistant. General access is set in{" "}
                      <Link to="/app/admin/settings">Settings</Link>.
                    </>
                  )}
                </div>
                {/* ADR-0213 Part 1a — the interim, said once: areas and the option are recorded and enforce nothing yet. */}
                <div className="help" style={{ marginTop: 4 }}>
                  A person reaches only the objects in their subject areas — or any view, with Query beyond the model. Someone with neither reaches no data.
                </div>

                {selectedRow && (
                  <div className="card" style={{ marginTop: 12 }}>
                    <PersonDetailPanel
                      row={selectedRow}
                      userCandidates={userOnlyCandidates}
                      memberCandidates={memberCandidates}
                      busy={detailBusy}
                      error={detailError}
                      solidManagerId={solidEdgeByAccount.get(selectedRow.account_id)?.manager_account_id ?? null}
                      solidManagerName={solidEdgeByAccount.get(selectedRow.account_id)?.manager_display_name ?? null}
                      dottedManagerIds={(dottedEdgesByAccount.get(selectedRow.account_id) ?? []).map((e) => e.manager_account_id)}
                      onClose={() => selectPerson(null)}
                      onSaveUser={(patch) => void saveUserDetail(selectedRow, patch)}
                      onSaveReportsTo={(target) => void saveReportsTo(selectedRow, target)}
                      onSaveDottedManagers={(ids) => void saveDottedManagers(selectedRow, ids)}
                      subjectAreaOptions={subjectAreaCandidates}
                      subjectAreaOptionsError={subjectAreasError}
                      onSaveSubjectAreas={(ids) => void saveSubjectAreas(selectedRow, ids)}
                      onSaveQueryBeyondModel={(value) => void saveQueryBeyondModel(selectedRow, value)}
                      onSaveAtlasQuery={(value) => void saveAtlasQuery(selectedRow, value)}
                      onResetPassword={() => void resetUserPassword(selectedRow)}
                      onSaveContact={(patch) => void saveContactDetail(selectedRow, patch)}
                      onPromoteContact={(entitlement) => void promoteContact(selectedRow, entitlement)}
                      onRevokeAccess={() => void openRevokeAccess(selectedRow)}
                      deliverability={deliverability.get(selectedRow.account_id) ?? null}
                      onClearDeliverability={() => void clearDeliverability(selectedRow)}
                      onOffboard={() => void openOffboard(selectedRow)}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Organization tab (ADR-0173) ──
            The canvas is an ENHANCEMENT, not the only path: every relationship
            it edits is equally editable via the keyboard-reachable fields on
            the user detail card (Reports to / Dotted-line managers). Contact
            proxies are edited on the contact's detail card (owner ruling
            2026-08-22 — the chart displays proxies, it does not edit them). */}
        {tab === "organization" && (
          <div className="tabPanel">
            {(usersError || orgError) && <ErrorState detail={usersError ?? orgError ?? ""} />}
            {!usersError && !orgError && (usersLoading || !data || !orgEdges) && <LoadingState />}
            {!usersError && !orgError && data && orgEdges && (
              <OrganizationCanvas
                members={data.members}
                edges={orgEdges}
                onDrawEdge={async (accountId, managerId) => {
                  const kind: "solid" | "dotted" = solidEdgeByAccount.has(accountId) ? "dotted" : "solid";
                  try {
                    await createOrgEdge(accountId, managerId, kind);
                  } catch (e) {
                    let message = orgApiError(e);
                    // Teaching refusal: the most common cycle refusal on a
                    // draw is a reversed gesture (dragging from the person up
                    // to their intended manager). Say the do-side.
                    if (message.includes("reporting cycle")) {
                      message += " The line starts at the manager: drag from the dot under the manager's box down onto the person who reports to them.";
                    }
                    toast.push({ kind: "error", message });
                  } finally {
                    void loadOrgEdges();
                  }
                }}
                onMakePrimary={async (edgeId) => {
                  try {
                    await patchOrgEdgeKind(edgeId, "solid");
                  } catch (e) {
                    toast.push({ kind: "error", message: orgApiError(e) });
                  } finally {
                    void loadOrgEdges();
                  }
                }}
                onMakeDotted={async (edgeId) => {
                  try {
                    await patchOrgEdgeKind(edgeId, "dotted");
                  } catch (e) {
                    toast.push({ kind: "error", message: orgApiError(e) });
                  } finally {
                    void loadOrgEdges();
                  }
                }}
                onRemoveEdge={async (edgeId) => {
                  try {
                    await removeOrgEdge(edgeId);
                  } catch (e) {
                    toast.push({ kind: "error", message: orgApiError(e) });
                  } finally {
                    void loadOrgEdges();
                  }
                }}
              />
            )}
          </div>
        )}

        {/* ── Groups tab ── */}
        {tab === "groups" && (
          <div className="tabPanel">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginBottom: 12 }}>
              <button className="btn btnPrimary" type="button" onClick={() => setCreateGroupModalOpen(true)}>
                Create team
              </button>
            </div>

            {groupsError && <ErrorState detail={groupsError} />}
            {groupsLoading && !groupRows.length ? <LoadingState /> : null}

            {!groupsError && groupRows.length === 0 && !groupsLoading ? (
              <div className="card">
                <div className="adminListEmpty">No teams yet. Create a team to manage nav visibility.</div>
              </div>
            ) : null}

            {!groupsError && groupRows.length > 0 ? (
              <div className="card tableWrap" style={{ marginBottom: 16 }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th style={{ textAlign: "center" }}>Status</th>
                      <th style={{ textAlign: "center" }}>Members</th>
                      <th style={{ width: 100 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupRows.map((row) => (
                      <tr
                        key={row.id}
                        style={{ background: selectedGroupId === row.id ? "var(--surface-2, rgba(0,0,0,0.04))" : undefined }}
                      >
                        <td>
                          <button type="button" className="adminListName" onClick={() => selectGroup(row.id)}>
                            {row.name}
                          </button>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <span className={row.status === "active" ? "pill pillOk" : "pill pillNeutral"}>
                            {row.status === "active" ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td style={{ textAlign: "center" }}>{row.member_count}</td>
                        <td>
                          <button type="button" className="adminListAction isDanger" onClick={() => void deleteGroup(row.id)}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            {/* Group detail panel */}
            {selectedGroupId ? (
              <div className="card" style={{ marginTop: 4 }}>
                {detailLoading ? <LoadingState /> : null}
                {!detailLoading && !detailGroup ? (
                  <div className="muted">Team not found.</div>
                ) : null}
                {!detailLoading && detailGroup ? (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: 12, alignItems: "flex-end", marginBottom: 20 }}>
                      <div className="field" style={{ marginBottom: 0 }}>
                        <label className="label">Team name</label>
                        <input
                          className="input"
                          defaultValue={detailGroup.name}
                          key={detailGroup.id + detailGroup.name}
                          onBlur={(e) => {
                            const v = e.target.value.trim();
                            if (v && v !== detailGroup.name) void patchGroup(detailGroup.id, { name: v });
                          }}
                        />
                      </div>
                      <div className="field" style={{ marginBottom: 0 }}>
                        <label className="label">Status</label>
                        <select
                          className="select"
                          value={detailGroup.status}
                          onChange={(e) => void patchGroup(detailGroup.id, { status: e.target.value as "active" | "inactive" })}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                      <button type="button" className="btn" style={{ alignSelf: "flex-end" }} onClick={() => selectGroup(null)}>
                        Close
                      </button>
                    </div>

                    <div style={{ borderTop: "1px solid var(--border)", paddingTop: 16 }}>
                      <div style={{ fontWeight: 600, marginBottom: 12 }}>Members</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 12, alignItems: "start" }}>
                        <div>
                          <div className="label" style={{ marginBottom: 6 }}>
                            Available
                            {tenantUsers.filter((u) => !memberIds.has(u.account_id)).length > 0 && (
                              <span className="muted" style={{ fontWeight: 400, marginLeft: 6 }}>
                                — hold Ctrl/⌘ to multi-select
                              </span>
                            )}
                          </div>
                          <select
                            multiple
                            size={8}
                            className="input"
                            style={{ width: "100%", height: 220 }}
                            value={availableSelected}
                            onChange={(e) => setAvailableSelected(Array.from(e.target.selectedOptions, (o) => o.value))}
                            disabled={memberOpsLoading}
                          >
                            {tenantUsers
                              .filter((u) => !memberIds.has(u.account_id))
                              .map((u) => (
                                <option key={u.account_id} value={u.account_id}>
                                  {userLabel(u)}
                                </option>
                              ))}
                          </select>
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", gap: 8, justifyContent: "center", paddingTop: 28 }}>
                          <button
                            type="button"
                            className="btn"
                            disabled={availableSelected.length === 0 || memberOpsLoading}
                            onClick={() => void addMembersToGroup()}
                          >
                            Add →
                          </button>
                          <button
                            type="button"
                            className="btn"
                            disabled={membersSelected.length === 0 || memberOpsLoading}
                            onClick={() => void removeMembersFromGroup()}
                          >
                            ← Remove
                          </button>
                        </div>

                        <div>
                          <div className="label" style={{ marginBottom: 6 }}>
                            In group
                            {detailMembers.length > 0 && (
                              <span className="muted" style={{ fontWeight: 400, marginLeft: 6 }}>
                                — hold Ctrl/⌘ to multi-select
                              </span>
                            )}
                          </div>
                          <select
                            multiple
                            size={8}
                            className="input"
                            style={{ width: "100%", height: 220 }}
                            value={membersSelected}
                            onChange={(e) => setMembersSelected(Array.from(e.target.selectedOptions, (o) => o.value))}
                            disabled={memberOpsLoading}
                          >
                            {detailMembers.map((m) => (
                              <option key={m.account_id} value={m.account_id}>
                                {userLabel(m)}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div style={{ borderTop: "1px solid var(--border)", paddingTop: 16 }}>
                      <SyncPolicyCard
                        title="Team document sync"
                        apiPath={`/api/tenant/admin/document-sync/teams/${detailGroup.id}`}
                        scope="team"
                        editable
                        notConnectedHint="Connect Microsoft 365 under Integrations first."
                      />
                    </div>
                  </>
                ) : null}
              </div>
            ) : (
              groupRows.length > 0 ? (
                <div className="muted" style={{ fontSize: 13, paddingTop: 4 }}>
                  Select a team above to manage its members.
                </div>
              ) : null
            )}
          </div>
        )}
      </PageLayout>

      {/* Add person modal — single entry point for user invites and contact creation. Spec §6.4. */}
      {createOpen && (
        <div className="modalOverlay" role="presentation" onClick={() => !createSaving && setCreateOpen(false)}>
          <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: 800, marginBottom: 16 }}>Add person</div>

            <div className="field" style={{ marginBottom: 12 }}>
              <span className="label">Type</span>
              <div role="radiogroup" aria-label="Person type" style={{ display: "flex", gap: 8 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="person-type"
                    value="user"
                    checked={createType === "user"}
                    onChange={() => setCreateType("user")}
                    disabled={createSaving}
                  />
                  <span>User (login)</span>
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="person-type"
                    value="contact"
                    checked={createType === "contact"}
                    onChange={() => setCreateType("contact")}
                    disabled={createSaving}
                  />
                  <span>Contact (no login)</span>
                </label>
              </div>
              <div className="help">
                {createType === "user"
                  ? "Users receive an invite email and consume a seat after acceptance."
                  : "Contacts are RASCI recipients only — they cannot log in. Route their in-system notifications to a user via Proxy."}
              </div>
            </div>

            <div className="field" style={{ marginBottom: 12 }}>
              <label className="label" htmlFor="create-display-name">Display name{createType === "user" ? <span className="muted" style={{ fontWeight: 400, marginLeft: 4 }}>(optional)</span> : ""}</label>
              <input
                id="create-display-name"
                className="input"
                value={createDisplayName}
                onChange={(e) => setCreateDisplayName(e.target.value)}
                autoFocus={createType === "contact"}
                disabled={createSaving}
                placeholder={createType === "user" ? "e.g. Jane Smith" : ""}
              />
            </div>

            <div className="field" style={{ marginBottom: 12 }}>
              <label className="label" htmlFor="create-email">Email</label>
              <input
                id="create-email"
                className="input"
                type="email"
                value={createEmail}
                onChange={(e) => setCreateEmail(e.target.value)}
                autoComplete="email"
                autoFocus={createType === "user"}
                disabled={createSaving}
              />
            </div>

            {createType === "user" && (
              <div className="field" style={{ marginBottom: 12 }}>
                <label className="label" htmlFor="create-entitlement">Entitlement</label>
                <select
                  id="create-entitlement"
                  className="select"
                  value={createEntitlement}
                  onChange={(e) => setCreateEntitlement(e.target.value as TenantEntitlement)}
                  disabled={createSaving}
                >
                  <option value="viewer">viewer</option>
                  <option value="builder">builder</option>
                  <option value="admin">admin</option>
                </select>
              </div>
            )}

            {createType === "contact" && (
              <div className="field" style={{ marginBottom: 12 }}>
                <label className="label">Proxy (required)</label>
                <MemberPicker
                  mode="single"
                  allowedKinds={["user"]}
                  candidates={userOnlyCandidates}
                  selected={createProxyId ? [createProxyId] : []}
                  onChange={(next) => setCreateProxyId(next[0] ?? null)}
                  placeholder="Select a user to route in-system notifications…"
                  required
                  disabled={createSaving}
                />
                <div className="help">Every contact must route in-system notifications to an Atlas user.</div>
              </div>
            )}

            {createError && <div className="help" style={{ color: "var(--danger-text)", marginBottom: 12 }}>{createError}</div>}

            <div className="actions">
              <button type="button" className="btn" onClick={() => setCreateOpen(false)} disabled={createSaving}>Cancel</button>
              <button
                type="button"
                className="btn btnPrimary"
                onClick={() => void submitCreate()}
                disabled={
                  createSaving
                  || createEmail.trim() === ""
                  || (createType === "contact" && (createDisplayName.trim() === "" || !createProxyId))
                  || (createType === "user" && createEmail.trim() === "")
                }
              >
                {createSaving ? "Saving…" : (createType === "user" ? "Send invite" : "Create contact")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADR-0204 — offboarding. Rendered in sentences, never as a field dump:
          an admin is deciding whether to strip someone off live accountability,
          and a JSON-shaped summary is not a basis for that. */}
      {offboard && (
        <div className="modalOverlay" role="presentation" onClick={() => !detailBusy && setOffboard(null)}>
          <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: 800, marginBottom: 12 }}>
              Offboard {offboard.preview.account.display_name ?? offboard.preview.account.email}
            </div>

            {offboard.preview.proxy_for.length > 0 ? (
              /* Progressive disclosure: check state first, show the relevant next
                 step. No disabled button with a tooltip explaining itself. */
              <>
                <div style={{ fontSize: 13, marginBottom: 12 }}>
                  This person is the notification proxy for{" "}
                  {offboard.preview.proxy_for.map((c) => c.display_name ?? "a contact").join(", ")}.
                  Offboarding them now would leave those contacts with no way to receive anything.
                  Repoint their proxies first.
                </div>
                <div className="actions">
                  <button type="button" className="btn" onClick={() => setOffboard(null)}>Close</button>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 13, marginBottom: 10 }}>
                  {offboard.preview.non_accountable_items > 0
                    ? `They hold a supporting role on ${offboard.preview.non_accountable_items} open action item${offboard.preview.non_accountable_items === 1 ? "" : "s"}. Those roles will be removed.`
                    : "They hold no supporting roles on open action items."}
                </div>

                {offboard.preview.accountable_items.length > 0 && (
                  <div className="field" style={{ marginBottom: 12 }}>
                    <span className="label">
                      They are accountable for {offboard.preview.accountable_items.length} open item
                      {offboard.preview.accountable_items.length === 1 ? "" : "s"}. Who takes them over?
                    </span>
                    <select
                      className="select"
                      value={offboard.successor ?? ""}
                      onChange={(e) => setOffboard({ ...offboard, successor: e.target.value || null })}
                      disabled={detailBusy}
                    >
                      <option value="">Choose a person…</option>
                      {userOnlyCandidates
                        .filter((c) => c.id !== offboard.row.account_id)
                        .map((c) => (
                          <option key={c.id} value={c.id}>{candidateLabel(c)}</option>
                        ))}
                    </select>
                  </div>
                )}

                <div style={{ fontSize: 13, marginBottom: 14 }}>
                  They will be removed from {offboard.preview.group_memberships.length} team
                  {offboard.preview.group_memberships.length === 1 ? "" : "s"} and{" "}
                  {offboard.preview.subscription_recipients.length} report subscription
                  {offboard.preview.subscription_recipients.length === 1 ? "" : "s"}.
                </div>

                <div className="actions">
                  <button
                    type="button"
                    className="btn btnPrimary"
                    onClick={() => void submitOffboard("disabled")}
                    disabled={detailBusy || (offboard.preview.accountable_items.length > 0 && !offboard.successor)}
                  >
                    Offboard and disable
                  </button>
                  {offboard.preview.account.kind === "contact" && (
                    <button
                      type="button"
                      className="btn btnDanger"
                      onClick={() => void submitOffboard("deleted")}
                      disabled={detailBusy || (offboard.preview.accountable_items.length > 0 && !offboard.successor)}
                      title="Deleting also removes them from the history of closed items"
                    >
                      Offboard and delete
                    </button>
                  )}
                  <button type="button" className="btn" onClick={() => setOffboard(null)} disabled={detailBusy}>
                    Cancel
                  </button>
                </div>
                {offboard.preview.account.kind === "contact" && (
                  <div className="help muted" style={{ marginTop: 8 }}>
                    Disabling keeps their name on closed items. Deleting removes it from those too.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Revoke access — user → contact. Mirrors the offboard dialog's shape
          (blocker first, then the decisions), but its closing sentence is the
          opposite one: what this person KEEPS. */}
      {revoke && (
        <div className="modalOverlay" role="presentation" onClick={() => !detailBusy && setRevoke(null)}>
          <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: 800, marginBottom: 12 }}>
              Revoke access for {revoke.preview.account.display_name ?? revoke.preview.account.email}
            </div>

            {revoke.preview.proxy_for.length > 0 ? (
              <>
                <div style={{ fontSize: 13, marginBottom: 12 }}>
                  This person is the notification proxy for{" "}
                  {revoke.preview.proxy_for.map((c) => c.display_name ?? "a contact").join(", ")}.
                  A contact cannot be anyone's proxy — it has no inbox to read. Repoint those contacts first.
                </div>
                <div className="actions">
                  <button type="button" className="btn" onClick={() => setRevoke(null)}>Close</button>
                </div>
              </>
            ) : (
              <>
                <div className="field" style={{ marginBottom: 12 }}>
                  <span className="label">Who receives their in-system notifications now?</span>
                  <select
                    className="select"
                    value={revoke.proxy ?? ""}
                    onChange={(e) => setRevoke({ ...revoke, proxy: e.target.value || null })}
                    disabled={detailBusy}
                  >
                    <option value="">Choose a person…</option>
                    {userOnlyCandidates
                      .filter((c) => c.id !== revoke.row.account_id)
                      .map((c) => (
                        <option key={c.id} value={c.id}>{candidateLabel(c)}</option>
                      ))}
                  </select>
                  <div className="help">Email still goes to them directly. Every contact needs a proxy for the rest.</div>
                </div>

                {revoke.preview.accountable_items.length > 0 && (
                  <div className="field" style={{ marginBottom: 12 }}>
                    <span className="label">
                      They are accountable for {revoke.preview.accountable_items.length} open item
                      {revoke.preview.accountable_items.length === 1 ? "" : "s"}. Who takes them over?
                    </span>
                    <select
                      className="select"
                      value={revoke.successor ?? ""}
                      onChange={(e) => setRevoke({ ...revoke, successor: e.target.value || null })}
                      disabled={detailBusy}
                    >
                      <option value="">Choose a person…</option>
                      {userOnlyCandidates
                        .filter((c) => c.id !== revoke.row.account_id)
                        .map((c) => (
                          <option key={c.id} value={c.id}>{candidateLabel(c)}</option>
                        ))}
                    </select>
                    <div className="help">A contact cannot be accountable — they cannot sign in to close the item.</div>
                  </div>
                )}

                <div style={{ fontSize: 13, marginBottom: 14 }}>
                  They keep their {revoke.preview.group_memberships.length} team
                  {revoke.preview.group_memberships.length === 1 ? "" : "s"},{" "}
                  {revoke.preview.subscription_recipients.length} report subscription
                  {revoke.preview.subscription_recipients.length === 1 ? "" : "s"} and every action item role
                  they hold. Their password is destroyed and any open session ends immediately — restoring
                  access later means a fresh invitation to set one.
                </div>

                <div className="actions">
                  <button
                    type="button"
                    className="btn btnPrimary"
                    onClick={() => void submitRevokeAccess()}
                    disabled={
                      detailBusy ||
                      !revoke.proxy ||
                      (revoke.preview.accountable_items.length > 0 && !revoke.successor)
                    }
                  >
                    {detailBusy ? "Working…" : "Revoke access"}
                  </button>
                  <button type="button" className="btn" onClick={() => setRevoke(null)} disabled={detailBusy}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Create group modal */}
      {createGroupModalOpen && (
        <div className="modalOverlay" role="presentation" onClick={() => !creatingGroup && setCreateGroupModalOpen(false)}>
          <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: 800, marginBottom: 16 }}>Create team</div>
            <div className="field" style={{ marginBottom: 16 }}>
              <label className="label" htmlFor="new-group-name">Team name</label>
              <input
                id="new-group-name"
                className="input"
                type="text"
                maxLength={100}
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && void createGroup()}
                autoFocus
                disabled={creatingGroup}
              />
            </div>
            <div className="actions">
              <button type="button" className="btn" onClick={() => { setCreateGroupModalOpen(false); setNewGroupName(""); }} disabled={creatingGroup}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btnPrimary"
                onClick={() => void createGroup()}
                disabled={creatingGroup || newGroupName.trim() === ""}
              >
                {creatingGroup ? "Creating…" : "Create team"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Detail card rendered beneath the Users tab table when a row is selected.
// Matches the groups-tab detail pattern already in use on this page.
function PersonDetailPanel(props: {
  row: TenantMemberRow;
  userCandidates: MemberCandidate[];
  memberCandidates: MemberCandidate[];
  busy: boolean;
  error: string | null;
  /** ADR-0173: the row's solid (primary) manager, from the org-edge table. */
  solidManagerId: string | null;
  solidManagerName: string | null;
  dottedManagerIds: string[];
  onClose: () => void;
  onSaveUser: (patch: { display_name?: string | null; escalates_to_account_id?: string | null }) => void;
  /** ADR-0173: upsert/remove the solid edge (org-edge routes, not the membership PATCH). */
  onSaveReportsTo: (targetAccountId: string | null) => void;
  onSaveDottedManagers: (managerAccountIds: string[]) => void;
  /** ADR-0212 §4.5 — the tenant's non-Master subject areas, and the save of a person's full set. */
  subjectAreaOptions: AreaCandidate[];
  subjectAreaOptionsError: string | null;
  onSaveSubjectAreas: (areaIds: string[]) => void;
  /** ADR-0213 — Query beyond the model, saved alone on each change. */
  onSaveQueryBeyondModel: (value: boolean) => void;
  /** ADR-0213 ruling 84 — Atlas Query, saved alone on each change. */
  onSaveAtlasQuery: (value: boolean) => void;
  onResetPassword: () => void;
  onSaveContact: (patch: { display_name?: string; email?: string; proxy_account_id?: string | null }) => void;
  onPromoteContact: (entitlement: TenantEntitlement) => void;
  onRevokeAccess: () => void;
  /** ADR-0203 — present only when the provider is refusing this person's address. */
  deliverability: DeliverabilityRow | null;
  onClearDeliverability: () => void;
  onOffboard: () => void;
}) {
  const { row, userCandidates, memberCandidates, busy, error, solidManagerId, solidManagerName, dottedManagerIds, onClose, onSaveUser, onSaveReportsTo, onSaveDottedManagers, subjectAreaOptions, subjectAreaOptionsError, onSaveSubjectAreas, onSaveQueryBeyondModel, onSaveAtlasQuery, onResetPassword, onSaveContact, onPromoteContact, onRevokeAccess, deliverability, onClearDeliverability, onOffboard } = props;
  const isContact = row.account_type === "contact";
  // The person's own areas always show as chips, even if the tenant's list failed to load or has moved on.
  const pickerCandidates = useMemo(() => {
    const known = new Set(subjectAreaOptions.map((c) => c.id));
    return [...subjectAreaOptions, ...(row.subject_areas ?? []).filter((a) => !known.has(a.id)).map((a) => ({ id: a.id, label: a.name }))];
  }, [subjectAreaOptions, row.subject_areas]);
  const invitePending =
    !row.invite?.consumed_at && (row.invite?.state === "pending" || row.invite?.state === "expired");
  const inviteExpired = row.invite?.state === "expired";

  // ADR-0213 Part 2 (spec §4.2) — what they reach, read from the hub's resolver
  // (never computed here): loaded when the panel opens and again after every
  // change to the person — the list is re-read after each save, so `row` is a
  // new object then. Users only: a contact reaches nothing.
  const [reach, setReach] = useState<EffectiveModel | null>(null);
  const [reachError, setReachError] = useState<string | null>(null);
  const [showReach, setShowReach] = useState(false);
  useEffect(() => {
    if (isContact) return;
    let cancelled = false;
    void apiFetch<EffectiveModel>(`/api/tenant/admin/users/${row.account_id}/effective-model`)
      .then((m) => {
        if (cancelled) return;
        setReach(m);
        setReachError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setReachError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [row, isContact]);
  // A person's line is only ever drawn from their own read: switching to
  // another person clears the last one's reach and error before theirs lands.
  useEffect(() => {
    setReach(null);
    setReachError(null);
    setShowReach(false);
  }, [row.account_id]);

  // User state
  const [userDisplayName, setUserDisplayName] = useState(row.display_name ?? "");
  const [escalatesSelected, setEscalatesSelected] = useState<string | null>(row.escalates_to_account_id);
  useEffect(() => {
    setUserDisplayName(row.display_name ?? "");
    setEscalatesSelected(row.escalates_to_account_id);
  }, [row.account_id, row.display_name, row.escalates_to_account_id]);

  // Contact state
  const [displayName, setDisplayName] = useState(row.display_name ?? "");
  const [email, setEmail] = useState(row.email);
  const [proxyId, setProxyId] = useState<string | null>(row.proxy_account_id);
  const [promoteEntitlement, setPromoteEntitlement] = useState<TenantEntitlement>("viewer");
  useEffect(() => {
    setDisplayName(row.display_name ?? "");
    setEmail(row.email);
    setProxyId(row.proxy_account_id);
    setPromoteEntitlement("viewer");
  }, [row.account_id, row.display_name, row.email, row.proxy_account_id]);

  const escalatesCandidates = useMemo(
    () => memberCandidates.filter((c) => c.id !== row.account_id),
    [memberCandidates, row.account_id]
  );

  const proxyCandidates = useMemo(
    () => userCandidates.filter((c) => c.id !== row.account_id),
    [userCandidates, row.account_id]
  );

  const userDirty =
    userDisplayName !== (row.display_name ?? "") ||
    escalatesSelected !== row.escalates_to_account_id;

  function saveUser() {
    const patch: { display_name?: string | null; escalates_to_account_id?: string | null } = {};
    if (userDisplayName !== (row.display_name ?? "")) {
      patch.display_name = userDisplayName.trim() || null;
    }
    if (escalatesSelected !== row.escalates_to_account_id) {
      patch.escalates_to_account_id = escalatesSelected;
    }
    if (Object.keys(patch).length > 0) onSaveUser(patch);
  }

  function saveContact() {
    const patch: { display_name?: string; email?: string; proxy_account_id?: string | null } = {};
    if (displayName !== (row.display_name ?? "")) patch.display_name = displayName;
    if (email !== row.email) patch.email = email;
    if (proxyId !== row.proxy_account_id) patch.proxy_account_id = proxyId;
    if (Object.keys(patch).length > 0) onSaveContact(patch);
  }

  const contactDirty =
    displayName !== (row.display_name ?? "") ||
    email !== row.email ||
    proxyId !== row.proxy_account_id;

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontWeight: 700 }}>
          {row.display_name ?? row.email} <span className="muted" style={{ fontWeight: 400, marginLeft: 8 }}>({isContact ? "Contact" : "User"})</span>
        </div>
        <button type="button" className="btn" onClick={onClose} disabled={busy}>Close</button>
      </div>

      {deliverability && (
        <div className="card" style={{ borderColor: "var(--danger-border, var(--danger-text))" }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Mail to this address is failing</div>
          <div style={{ fontSize: 13, marginBottom: 8 }}>
            {deliverability.reason === "complaint"
              ? "The recipient marked our mail as spam"
              : "The mail provider reported this address as permanently undeliverable"}
            {" on "}
            {new Date(deliverability.first_observed_at).toLocaleString()}
            {deliverability.observation_count > 1 ? `, and ${deliverability.observation_count} times since.` : "."}
          </div>
          <div style={{ fontSize: 13, marginBottom: 10 }}>
            Atlas has stopped sending to it. Correcting the address below clears this on its own.{" "}
            {deliverability.subtype === "OnAccountSuppressionList" || deliverability.subtype === "OnTenantSuppressionList"
              ? "This address is also held on the mail provider's own suppression list — clearing it here alone will not make delivery work, it has to be removed there too."
              : "To keep this address, clear the block and Atlas will try again."}
          </div>
          <button type="button" className="btn" onClick={onClearDeliverability} disabled={busy}>
            Clear and try again
          </button>
        </div>
      )}

      {isContact ? (
        <>
          <label className="field">
            <span className="label">Display name</span>
            <input className="input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} disabled={busy} />
          </label>
          <label className="field">
            <span className="label">Email</span>
            <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} />
          </label>
          <div className="field">
            <span className="label">Proxy (required)</span>
            <MemberPicker
              mode="single"
              allowedKinds={["user"]}
              candidates={proxyCandidates}
              selected={proxyId ? [proxyId] : []}
              onChange={(next) => setProxyId(next[0] ?? null)}
              placeholder="Select a user to route in-system notifications…"
              required
              disabled={busy}
            />
            <div className="help">Every contact must route in-system notifications to an Atlas user. Email still goes to the contact directly. The proxy also anchors this contact on the org chart.</div>
          </div>
          {error && <div className="help" style={{ color: "var(--danger-text)" }}>{error}</div>}
          <div className="actions">
            <button type="button" className="btn btnPrimary" onClick={saveContact} disabled={busy || !contactDirty || !proxyId}>
              {busy ? "Saving…" : "Save"}
            </button>
          </div>

          {/* Promotion sits next to the proxy editor because the proxy is what
              it retires: this is the gesture for when the person Atlas has
              only been emailing comes inside. */}
          <div style={{ borderTop: "1px solid var(--border)", paddingTop: 10, marginTop: 4 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Promote to user</div>
            <div className="help muted" style={{ marginBottom: 8 }}>
              Invites them to set up a sign-in. They stay a contact until they accept — keeping their proxy
              and their email — and keep everything that already names them: open and closed action items,
              teams, report subscriptions.
            </div>
            {invitePending && (
              <div className="help" style={{ marginBottom: 8 }}>
                Invited {formatTimestamp(row.invite!.created_at)}
                {inviteExpired ? ", and the link has expired." : " — not accepted yet."}
                {" "}Resend it from their row in the list, or send a new one here.
              </div>
            )}
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              <label className="field" style={{ marginBottom: 0 }}>
                <span className="label">Entitlement</span>
                <select
                  className="select"
                  value={promoteEntitlement}
                  onChange={(e) => setPromoteEntitlement(e.target.value as TenantEntitlement)}
                  disabled={busy}
                  aria-label={`Entitlement after promoting ${row.display_name ?? row.email}`}
                >
                  <option value="viewer">viewer</option>
                  <option value="builder">builder</option>
                  <option value="admin">admin</option>
                </select>
              </label>
              <button
                type="button"
                className="btn"
                onClick={() => onPromoteContact(promoteEntitlement)}
                disabled={busy || contactDirty}
              >
                {invitePending ? "Send a new invite" : "Send invite"}
              </button>
            </div>
            {/* Not a nicety: the invitation is emailed to whatever address is
                STORED, so inviting over an unsaved email edit would send it to
                the old one. */}
            {contactDirty && (
              <div className="help" style={{ marginTop: 6 }}>
                Save your changes first — the invitation goes to the stored address, not what is typed here.
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <label className="field">
            <span className="label">Display name</span>
            <input
              className="input"
              value={userDisplayName}
              onChange={(e) => setUserDisplayName(e.target.value)}
              disabled={busy}
              placeholder="e.g. Jane Smith"
            />
          </label>
          <label className="field">
            <span className="label">Reports to</span>
            <select
              className="select"
              value={solidManagerId ?? ""}
              onChange={(e) => onSaveReportsTo(e.target.value || null)}
              disabled={busy}
            >
              <option value="">—</option>
              {userCandidates
                .filter((c) => c.id !== row.account_id)
                .map((c) => (
                  <option key={c.id} value={c.id}>{candidateLabel(c)}</option>
                ))}
            </select>
            <div className="help">Their primary (solid-line) manager on the org chart.</div>
          </label>
          <div className="field">
            <span className="label">Dotted-line managers</span>
            <MemberPicker
              mode="multi"
              allowedKinds={["user"]}
              candidates={userCandidates.filter((c) => c.id !== row.account_id && c.id !== solidManagerId)}
              selected={dottedManagerIds}
              onChange={(next) => onSaveDottedManagers(next)}
              placeholder="Add additional managers…"
              disabled={busy}
            />
          </div>
          <div className="field">
            <span className="label">Escalates to (override)</span>
            <MemberPicker
              mode="single"
              allowedKinds={["user"]}
              candidates={escalatesCandidates}
              selected={escalatesSelected ? [escalatesSelected] : []}
              onChange={(next) => setEscalatesSelected(next[0] ?? null)}
              placeholder="Select a user…"
              disabled={busy}
            />
            <div className="help">Defaults to their manager. Set only to override the escalation path.</div>
            {escalatesSelected === null && solidManagerName && (
              <div className="help muted">Currently: {solidManagerName} (default)</div>
            )}
          </div>
          <div className="field">
            <span className="label">Subject areas</span>
            <AreaPicker
              candidates={pickerCandidates}
              selected={(row.subject_areas ?? []).map((a) => a.id)}
              onChange={(next) => onSaveSubjectAreas(next)}
              placeholder="Add a subject area…"
              disabled={busy}
            />
            {subjectAreaOptionsError && <div className="help">{subjectAreaOptionsError}</div>}
          </div>
          <div className="field">
            <label className="checkRow">
              <input
                type="checkbox"
                checked={row.query_beyond_model === true}
                onChange={(e) => onSaveQueryBeyondModel(e.target.checked)}
                disabled={busy}
              />
              <span>Query beyond the model</span>
            </label>
            <div className="help">Lets them query views outside their subject areas. Columns restricted for them stay restricted.</div>
          </div>
          {(row.entitlement === "builder" || row.entitlement === "admin") && (
            <div className="field">
              <label className="checkRow">
                <input
                  type="checkbox"
                  checked={row.atlas_query === true}
                  onChange={(e) => onSaveAtlasQuery(e.target.checked)}
                  disabled={busy}
                />
                <span>Atlas Query</span>
              </label>
              <div className="help">Lets them use Atlas Query. What they can query is set by their subject areas.</div>
            </div>
          )}
          {/* ADR-0213 Part 2 — the reach line, read-only, and what they reach. */}
          {(reach || reachError) && (
            <div className="field">
              {reachError ? <div className="help" style={{ color: "var(--danger-text)" }}>{reachError}</div> : null}
              {reach ? <div>{reachLine(reach)}</div> : null}
              {reach && reach.objects.length > 0 && (
                <>
                  <button type="button" className="disclosureToggle" aria-expanded={showReach} onClick={() => setShowReach((v) => !v)}>
                    Show what they reach
                  </button>
                  {showReach && (
                    <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                      {reach.objects.map((o) => (
                        <li key={o.name}>
                          {o.name}
                          {o.area_names.length > 0 && <span className="muted"> — {o.area_names.join(", ")}</span>}
                          {o.restricted_columns.length > 0 && <div className="help">Restricted: {o.restricted_columns.join(", ")}</div>}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          )}
          {error && <div className="help" style={{ color: "var(--danger-text)" }}>{error}</div>}
          <div className="actions">
            <button
              type="button"
              className="btn"
              onClick={() => setEscalatesSelected(null)}
              disabled={busy || escalatesSelected === null}
            >
              Clear escalation
            </button>
            <button
              type="button"
              className="btn"
              onClick={onResetPassword}
              disabled={busy || row.status === "invited" || row.status === "disabled"}
              title={row.status === "invited" ? "User has not accepted their invite yet" : row.status === "disabled" ? "User is disabled" : ""}
            >
              Send password reset
            </button>
            <button
              type="button"
              className="btn btnPrimary"
              onClick={saveUser}
              disabled={busy || !userDirty}
            >
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </>
      )}

      {!isContact && (
        /* The inverse of the contact card's Promote. Placed here for the same
           reason: this is where a person's kind is decided. */
        <div style={{ borderTop: "1px solid var(--border)", paddingTop: 10, marginTop: 4 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Revoke access</div>
          <div className="help muted" style={{ marginBottom: 8 }}>
            Turns them back into a contact: they keep every action item role and report subscription they
            are named on and carry on receiving email, but lose their sign-in, their assistant connections
            and their entitlement.
          </div>
          <button type="button" className="btn" onClick={onRevokeAccess} disabled={busy}>
            Revoke access…
          </button>
        </div>
      )}

      {/* ADR-0204 — one trigger for this action, and only here. Offboarding is
          not a drive-by toolbar button. */}
      <div style={{ borderTop: "1px solid var(--border)", paddingTop: 10, marginTop: 4 }}>
        <button type="button" className="btn btnDanger" onClick={onOffboard} disabled={busy}>
          Offboard…
        </button>
        <div className="help muted" style={{ marginTop: 6 }}>
          Removes them from open action items, teams and report subscriptions. Closed items keep their history.
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ADR-0173 — Organization tab canvas
// ---------------------------------------------------------------------------
//
// A design surface, not the only editing path (see the tab's comment above).
// Everything rendered derives from members + edges via the pure
// `computeOrgLayout`; positions are always computed, nothing persisted.
// Autosave house rule: every gesture persists immediately; failures surface
// the server's message verbatim in an error toast and the canvas simply
// re-renders from the unchanged data (no optimistic state to revert).

type OrgUserNodeData = {
  cell: OrgPersonCell;
};
type OrgStackNodeData = {
  members: OrgPersonCell[];
  expanded: boolean;
  onToggle: () => void;
};
type OrgContactsNodeData = {
  proxyName: string | null;
  contacts: Array<{ accountId: string; name: string; inactive: boolean }>;
};

function StatusPill({ cell }: { cell: OrgPersonCell }) {
  if (!cell.inactive) return null;
  return (
    <span className={cell.status === "invited" ? "pill pillWarn" : "pill pillBad"}>
      {cell.status.charAt(0).toUpperCase() + cell.status.slice(1)}
    </span>
  );
}

/**
 * A person on the chart. Interaction model (owner rulings 2026-08-22/23):
 *   * BOXES DO NOT MOVE — positions are always computed (the owner withdrew
 *     the drag-to-re-parent gesture 2026-08-23: a box that snaps back reads
 *     as broken, not as a gesture);
 *   * drag from the dot under a manager and drop ANYWHERE on the target box
 *     to set a reporting line;
 *   * dotted (matrix) lines enter box SIDES; the solid spine enters top/bottom.
 * The side/top handles are invisible zero-size ANCHORS — they never move,
 * so nothing shifts on hover.
 */
function OrgUserNode({ id, data }: NodeProps) {
  const d = data as unknown as OrgUserNodeData;
  const connection = useConnection();
  const isConnectTarget = connection.inProgress && connection.fromNode?.id !== id;
  return (
    <div
      className="orgUserBox"
      style={{ opacity: d.cell.inactive ? 0.6 : 1 }}
    >
      <Handle id="t-top" type="target" position={Position.Top} className="orgAnchor" isConnectable={false} />
      <Handle id="t-left" type="target" position={Position.Left} className="orgAnchor" isConnectable={false} />
      <Handle id="t-right" type="target" position={Position.Right} className="orgAnchor" isConnectable={false} />
      <Handle id="s-left" type="source" position={Position.Left} className="orgAnchor" isConnectable={false} />
      <Handle id="s-right" type="source" position={Position.Right} className="orgAnchor" isConnectable={false} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="orgName">{d.cell.name}</div>
        <div className="orgRole">{d.cell.entitlement}</div>
      </div>
      <StatusPill cell={d.cell} />
      <Handle
        id="s-bottom"
        type="source"
        position={Position.Bottom}
        className="orgDrawHandle"
        title="Drag to the person who should report to them"
      />
      {isConnectTarget && (
        <Handle id="t-box" type="target" position={Position.Top} className="orgBoxTarget" isConnectableStart={false} />
      )}
    </div>
  );
}

function OrgStackNode({ data }: NodeProps) {
  const d = data as unknown as OrgStackNodeData;
  return (
    <div className="orgGroupBox">
      <Handle id="t-top" type="target" position={Position.Top} className="orgAnchor" isConnectable={false} />
      <div className="orgGroupHeader">
        <span>{d.members.length} people</span>
        <button
          type="button"
          className="btn btnSm"
          aria-expanded={d.expanded}
          onClick={d.onToggle}
          title="Expand into individual boxes to edit their reporting lines"
        >
          {d.expanded ? "▾" : "▸"}
        </button>
      </div>
      <div style={{ display: "grid", gap: 2 }}>
        {/* Dense summary rows: name + the standard status pill when the
            person is not active (one status format platform-wide — owner,
            2026-08-23). The entitlement pill stays off at this density; role
            is on the hover title, the expanded boxes, and the Users table. */}
        {d.members.map((m) => (
          <div key={m.accountId} className="orgGroupRow" style={{ opacity: m.inactive ? 0.6 : 1 }}>
            <span className="orgRowName">{m.name}</span>
            <StatusPill cell={m} />
          </div>
        ))}
      </div>
    </div>
  );
}

function OrgContactsNode({ data }: NodeProps) {
  const d = data as unknown as OrgContactsNodeData;
  return (
    <div className="orgGroupBox orgContactsBox">
      <Handle id="t-top" type="target" position={Position.Top} className="orgAnchor" isConnectable={false} />
      <div
        className="orgGroupHeader"
        title={
          d.proxyName
            ? `These contacts route in-system notifications to ${d.proxyName}. Change a contact\u2019s proxy from their detail card on the Users tab.`
            : "These contacts have no proxy \u2014 in-system notifications have nowhere to route. Set a proxy on each contact\u2019s detail card on the Users tab."
        }
      >
        <span className="orgRowName">{d.proxyName ? "Contacts" : "Contacts \u00b7 no proxy"}</span>
      </div>
      <div style={{ display: "grid", gap: 2 }}>
        {d.contacts.map((c) => (
          <div key={c.accountId} className="orgGroupRow" style={{ opacity: c.inactive ? 0.6 : 1 }}>
            <span className="orgRowName">{c.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function OrgLabelNode({ data }: NodeProps) {
  const d = data as unknown as { text: string };
  return (
    <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.04em" }}>
      {d.text}
    </div>
  );
}

/**
 * A person in a picker needs BOTH halves. `display_name` already falls back to
 * the address when someone has no name, so a list built from it alone shows a
 * name for some rows and an address for others — and two people who share a
 * name are indistinguishable. Name first, address in parentheses.
 */
function candidateLabel(c: MemberCandidate): string {
  return c.email && c.email !== c.display_name ? `${c.display_name} (${c.email})` : c.display_name;
}

const ORG_NODE_TYPES = { orgUser: OrgUserNode, orgStack: OrgStackNode, orgContacts: OrgContactsNode, orgLabel: OrgLabelNode };

/** A box's bottom edge. Stacks and the contacts box grow with their contents. */
function boxBottom(b: OrgBox): number {
  if (b.kind === "stack") return b.y + stackBoxHeight(b.members.length);
  if (b.kind === "contacts") return b.y + contactsBoxHeight(b.contacts.length);
  return b.y + USER_BOX_HEIGHT;
}

/**
 * An orthogonal path that leaves the source's side, runs the span at `y`, and
 * comes back into the target's side — right angles with rounded corners, the
 * same vocabulary as the solid spine.
 */
function detourPath(sx: number, sy: number, tx: number, ty: number, y: number): string {
  const r = 10;
  const stub = 18;
  const dirX = tx >= sx ? 1 : -1;
  const dirY = y >= sy ? 1 : -1;
  const x1 = sx + dirX * stub;
  const x2 = tx - dirX * stub;
  return [
    `M ${sx},${sy}`,
    `H ${x1 - dirX * r}`,
    `Q ${x1},${sy} ${x1},${sy + dirY * r}`,
    `V ${y - dirY * r}`,
    `Q ${x1},${y} ${x1 + dirX * r},${y}`,
    `H ${x2 - dirX * r}`,
    `Q ${x2},${y} ${x2},${y - dirY * r}`,
    `V ${ty + dirY * r}`,
    `Q ${x2},${ty} ${x2 + dirX * r},${ty}`,
    `H ${tx}`
  ].join(" ");
}

/**
 * Dotted (matrix) edge.
 *
 * `getBezierPath` CANNOT curve this one. For Left/Right anchors it derives the
 * control-point offset from the HORIZONTAL gap between them, and a dotted
 * manager usually sits in the same column as their report — gap ~0, so the
 * control points collapse onto the endpoints and the "bezier" renders as a
 * straight line down the column, cutting across the solid spine's rail. No
 * curvature setting can fix that: it is multiplying zero.
 *
 * So the bow is drawn by hand for that case, out the side the two anchors are
 * already on, widening a little with the drop. Deliberately gentle (owner
 * ruling: a wide sweep is worse than the straight line) — just enough to take
 * the edge off and read as a second kind of relationship, not a fault.
 */
function OrgDottedEdge(props: EdgeProps) {
  // Set by the edge builder when boxes stand between these two on the same
  // rank. A straight line there runs BEHIND them — they are filled, so it
  // vanishes and surfaces only in the gaps, reading as fragments from nowhere.
  const d = props.data as
    | { detourY?: number; sourceAnchorY?: number; targetAnchorY?: number }
    | undefined;
  // The builder assigns the height on each side so two dotted edges never meet
  // one box at the same point. Only the x comes from the handle.
  const sourceY = d?.sourceAnchorY ?? props.sourceY;
  const targetY = d?.targetAnchorY ?? props.targetY;
  const detourY = d?.detourY;
  if (typeof detourY === "number") {
    return (
      <BaseEdge
        id={props.id}
        path={detourPath(props.sourceX, sourceY, props.targetX, targetY, detourY)}
        style={props.style}
        markerEnd={props.markerEnd}
        interactionWidth={props.interactionWidth}
      />
    );
  }
  const sourcePosition = props.sourcePosition ?? Position.Right;
  const targetPosition = props.targetPosition ?? Position.Right;
  const dy = Math.abs(targetY - sourceY);
  const sameSide = sourcePosition === targetPosition;
  if (sameSide && Math.abs(props.targetX - props.sourceX) < BOX_WIDTH * 0.75) {
    const dir = sourcePosition === Position.Left ? -1 : 1;
    const bow = dir * Math.min(60, 26 + dy * 0.07);
    const hand = `M ${props.sourceX},${sourceY} C ${props.sourceX + bow},${sourceY} ${props.targetX + bow},${targetY} ${props.targetX},${targetY}`;
    return (
      <BaseEdge
        id={props.id}
        path={hand}
        style={props.style}
        markerEnd={props.markerEnd}
        interactionWidth={props.interactionWidth}
      />
    );
  }
  // Boxes in different columns: there IS a horizontal gap, so the built-in
  // bezier behaves.
  const [path] = getBezierPath({
    sourceX: props.sourceX,
    sourceY,
    targetX: props.targetX,
    targetY,
    sourcePosition,
    targetPosition,
    curvature: 0.35
  });
  return (
    <BaseEdge
      id={props.id}
      path={path}
      style={props.style}
      markerEnd={props.markerEnd}
      interactionWidth={props.interactionWidth}
    />
  );
}

/**
 * Solid-spine edge: smoothstep whose horizontal run sits just above the
 * target box — children hang from a shared rail, the classic org-chart look.
 */
function OrgSmoothEdge(props: EdgeProps) {
  const [path] = getSmoothStepPath({
    sourceX: props.sourceX,
    sourceY: props.sourceY,
    targetX: props.targetX,
    targetY: props.targetY,
    sourcePosition: props.sourcePosition ?? Position.Bottom,
    targetPosition: props.targetPosition ?? Position.Top,
    borderRadius: 6,
    centerY: props.targetY - 20
  });
  return (
    <BaseEdge
      id={props.id}
      path={path}
      style={props.style}
      markerEnd={props.markerEnd}
      interactionWidth={props.interactionWidth}
    />
  );
}

const ORG_EDGE_TYPES = { orgSmooth: OrgSmoothEdge, orgDotted: OrgDottedEdge };

/** Estimated box height per kind — mirrors the layout module's constants. */
function orgBoxHeight(box: OrgBox): number {
  if (box.kind === "user") return USER_BOX_HEIGHT;
  if (box.kind === "stack") return stackBoxHeight(box.members.length);
  return contactsBoxHeight(box.contacts.length);
}

function OrganizationCanvas(props: {
  members: TenantMemberRow[];
  edges: OrgEdgeRow[];
  onDrawEdge: (accountId: string, managerAccountId: string) => void;
  onMakePrimary: (edgeId: string) => void;
  onMakeDotted: (edgeId: string) => void;
  onRemoveEdge: (edgeId: string) => void;
}) {
  const toast = useToast();
  const [expandedStacks, setExpandedStacks] = useState<Set<string>>(new Set());
  const [edgeMenu, setEdgeMenu] = useState<{ edgeId: string; kind: "solid" | "dotted"; left: number; top: number } | null>(null);
  // Truncated-name tooltip (house rule: tooltip-on-truncate). Native `title`
  // misfires on a zoomed canvas — tiny hover regions pop tooltips for
  // neighboring rows — so this renders ONE tooltip for exactly the hovered
  // name, only when that name is actually clipped.
  const [nameTip, setNameTip] = useState<{ text: string; left: number; top: number } | null>(null);
  const handleNameHover = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const span = target.closest?.(".orgRowName, .orgName") as HTMLElement | null;
    if (!span || !wrapRef.current) {
      setNameTip(null);
      return;
    }
    if (span.scrollWidth <= span.clientWidth + 1) {
      setNameTip(null);
      return;
    }
    const wrap = wrapRef.current.getBoundingClientRect();
    const r = span.getBoundingClientRect();
    setNameTip({
      text: span.textContent ?? "",
      left: r.left - wrap.left + r.width / 2,
      top: r.top - wrap.top
    });
  }, []);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const layout = useMemo(() => {
    const layoutMembers = props.members.map((m) => ({
      account_id: m.account_id,
      display_name: m.display_name,
      email: m.email,
      entitlement: m.entitlement,
      status: m.status,
      account_type: m.account_type,
      proxy_account_id: m.proxy_account_id
    }));
    const layoutEdges: OrgEdgeInput[] = props.edges.map((e) => ({
      id: e.id,
      account_id: e.account_id,
      manager_account_id: e.manager_account_id,
      kind: e.kind
    }));
    return computeOrgLayout(layoutMembers, layoutEdges, expandedStacks);
  }, [props.members, props.edges, expandedStacks]);

  const kindByEdgeId = useMemo(() => new Map(props.edges.map((e) => [e.id, e.kind])), [props.edges]);
  const boxById = useMemo(() => new Map(layout.boxes.map((b) => [b.id, b])), [layout]);

  const toggleStack = useCallback((managerId: string) => {
    setExpandedStacks((prev) => {
      const next = new Set(prev);
      if (next.has(managerId)) next.delete(managerId);
      else next.add(managerId);
      return next;
    });
  }, []);

  const builtNodes: FlowNode[] = useMemo(
    () =>
      layout.boxes.map((box: OrgBox): FlowNode => {
        if (box.kind === "user") {
          return {
            id: box.id,
            type: "orgUser",
            position: { x: box.x, y: box.y },
            draggable: false,
            data: {
              cell: {
                accountId: box.accountId,
                name: box.name,
                entitlement: box.entitlement,
                inactive: box.inactive,
                status: box.status
              }
            } as unknown as Record<string, unknown>
          };
        }
        if (box.kind === "stack") {
          return {
            id: box.id,
            type: "orgStack",
            position: { x: box.x, y: box.y },
            draggable: false,
            connectable: false,
            data: {
              members: box.members,
              expanded: false,
              onToggle: () => toggleStack(box.managerAccountId)
            } as unknown as Record<string, unknown>
          };
        }
        return {
          id: box.id,
          type: "orgContacts",
          position: { x: box.x, y: box.y },
          draggable: false,
          connectable: false,
          data: {
            proxyName: box.proxyAccountId
              ? (() => {
                  const m = props.members.find((x) => x.account_id === box.proxyAccountId);
                  return m ? (m.display_name ?? m.email) : null;
                })()
              : null,
            contacts: box.contacts
          } as unknown as Record<string, unknown>
        };
      }).concat(
        layout.labels.map((l): FlowNode => ({
          id: l.id,
          type: "orgLabel",
          position: { x: l.x, y: l.y },
          draggable: false,
          selectable: false,
          connectable: false,
          data: { text: l.text } as unknown as Record<string, unknown>
        }))
      ),
    [layout, props.members, toggleStack]
  );

  const flowEdges: FlowEdge[] = useMemo(
    () => {
      /** Which side of each box a dotted edge leaves and enters. */
      const sidesFor = (e: { sourceBoxId: string; targetBoxId: string }) => {
        const s = boxById.get(e.sourceBoxId);
        const t = boxById.get(e.targetBoxId);
        const srcRightOfTgt = (s?.x ?? 0) > (t?.x ?? 0) + BOX_WIDTH - 1;
        const tgtRightOfSrc = (t?.x ?? 0) > (s?.x ?? 0) + BOX_WIDTH - 1;
        if (tgtRightOfSrc) return { sourceSide: "right", targetSide: "left" };
        if (srcRightOfTgt) return { sourceSide: "left", targetSide: "right" };
        return { sourceSide: "right", targetSide: "right" };
      };

      // Two dotted edges meeting the same side of the same box at the same
      // height are unreadable: two lines and two arrowheads in one patch of
      // canvas, with nothing to say which arrow belongs to which line. So each
      // gets its own rung.
      //
      // The rung belongs to the EDGE, not to the side. Numbering per side
      // instead lets one edge be told "rung 0" at one end and "rung 1" at the
      // other, which draws it as a slant — and a short slanted dash between two
      // boxes on the same rank just reads as crooked. One number, used at both
      // ends, keeps every same-rank edge horizontal.
      const taken = new Map<string, Set<number>>();
      const rungOf = new Map<string, number>();
      let widest = 0;
      for (const e of layout.edges) {
        if (e.kind !== "dotted") continue;
        const { sourceSide, targetSide } = sidesFor(e);
        const keys = [`${e.sourceBoxId}|${sourceSide}`, `${e.targetBoxId}|${targetSide}`];
        let rung = 0;
        while (keys.some((k) => taken.get(k)?.has(rung))) rung++;
        for (const k of keys) {
          const set = taken.get(k);
          if (set) set.add(rung);
          else taken.set(k, new Set([rung]));
        }
        rungOf.set(e.id, rung);
        widest = Math.max(widest, rung);
      }
      const RUNG_STEP = 13;
      const dottedAnchorY = (boxId: string, _side: string, edgeId: string): number | undefined => {
        const rung = rungOf.get(edgeId);
        const box = boxById.get(boxId);
        if (rung === undefined || !box) return undefined;
        const height = boxBottom(box) - box.y;
        // Spread about the middle, using one chart-wide centre so both ends of
        // an edge between equal-height boxes land at the same y.
        const y = box.y + height / 2 + (rung - widest / 2) * RUNG_STEP;
        // Never off the box: a tall stack has room for many, a user box does not.
        return Math.min(Math.max(y, box.y + 9), box.y + height - 9);
      };

      return layout.edges.map((e): FlowEdge => {
        // The folded stack edge and the proxy connector are rendering-only;
        // real org edges open the action popover. The proxy connector draws
        // EXACTLY like a solid reporting line (owner ruling 2026-08-22) —
        // the contacts box's dashed outline is what marks it as different.
        const interactive = !e.id.startsWith("stackedge:") && !e.id.startsWith("proxyedge:");
        const base = {
          id: e.id,
          source: e.sourceBoxId,
          target: e.targetBoxId,
          markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color: "#64748b" },
          interactionWidth: interactive ? 24 : 0,
          selectable: interactive
        };
        if (e.kind === "solid" || e.kind === "proxy") {
          return {
            ...base,
            type: "orgSmooth",
            sourceHandle: "s-bottom",
            targetHandle: "t-top",
            style: { stroke: "#64748b", strokeWidth: 1.6 }
          };
        }
        // Dotted (matrix) line — enters the box SIDE, the org-chart
        // convention that keeps it visually apart from the solid spine.
        const src = boxById.get(e.sourceBoxId);
        const tgt = boxById.get(e.targetBoxId);
        const { sourceSide, targetSide } = sidesFor(e);
        // The offset anchors exist on the PERSON box only. A dotted line into a
        // stack or the contacts box uses the plain side anchor, because asking
        // xyflow for a handle a node does not have drops the edge silently.
        const sourceAnchorY = dottedAnchorY(e.sourceBoxId, sourceSide, e.id);
        const targetAnchorY = dottedAnchorY(e.targetBoxId, targetSide, e.id);

        // Same rank, with something standing between them: the straight line
        // would run behind those boxes. Work out a lane under the row and hand
        // it to the edge — only this side can see it, because the edge
        // component gets two points and no knowledge of a third box.
        let detourY: number | undefined;
        if (src && tgt && Math.abs(src.y - tgt.y) < 8) {
          const spanFrom = Math.min(src.x, tgt.x) + BOX_WIDTH;
          const spanTo = Math.max(src.x, tgt.x);
          const blockers = layout.boxes.filter(
            (b) =>
              b.id !== src.id &&
              b.id !== tgt.id &&
              Math.abs(b.y - src.y) < 8 &&
              b.x < spanTo &&
              b.x + BOX_WIDTH > spanFrom
          );
          if (blockers.length > 0) {
            detourY = Math.max(...[src, tgt, ...blockers].map(boxBottom)) + 26;
          }
        }

        return {
          ...base,
          type: "orgDotted",
          sourceHandle: `s-${sourceSide}`,
          targetHandle: `t-${targetSide}`,
          data: {
            ...(detourY !== undefined ? { detourY } : {}),
            ...(sourceAnchorY !== undefined ? { sourceAnchorY } : {}),
            ...(targetAnchorY !== undefined ? { targetAnchorY } : {})
          },
          style: { stroke: "#64748b", strokeDasharray: "5 4", strokeWidth: 1.4 }
        };
      });
    },
    [layout, boxById]
  );

  const onConnect = useCallback(
    (conn: Connection) => {
      // Drag runs manager (bottom dot) → report (anywhere on the box).
      if (!conn.source?.startsWith("user:") || !conn.target?.startsWith("user:")) return;
      const managerId = conn.source.slice("user:".length);
      const accountId = conn.target.slice("user:".length);
      if (managerId === accountId) return;
      props.onDrawEdge(accountId, managerId);
    },
    [props]
  );

  // A connection dropped on a STACK is refused by design (stacks are not
  // connectable — every line runs box-to-box). Silence there reads as broken;
  // teach the expand step instead.
  const onConnectEnd = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const el = (event.target as HTMLElement | null)?.closest?.('[data-id^="stack:"]');
      if (el) {
        toast.push({
          kind: "error",
          message: "That person is inside a stack \u2014 expand it with the chevron, then draw the line to their box."
        });
      }
    },
    [toast]
  );

  const onEdgeClick = useCallback((event: React.MouseEvent, edge: FlowEdge) => {
    const kind = kindByEdgeId.get(edge.id);
    if (!kind) return; // the folded stack edge — expand the stack to edit
    const rect = wrapRef.current?.getBoundingClientRect();
    setEdgeMenu({
      edgeId: edge.id,
      kind,
      left: event.clientX - (rect?.left ?? 0),
      top: event.clientY - (rect?.top ?? 0)
    });
  }, [kindByEdgeId]);

  return (
    <div className="card" style={{ padding: 0, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderBottom: "1px solid var(--border)" }}>
        <span className="help" style={{ padding: 0 }}>
          Drag from the dot under a manager to a person to set their reporting line. Solid lines set the default escalation path.
        </span>
        {expandedStacks.size > 0 && (
          <button type="button" className="btn btnSm" onClick={() => setExpandedStacks(new Set())}>
            Collapse stacks
          </button>
        )}
      </div>
      <div
        ref={wrapRef}
        className="orgChartCanvas"
        style={{ position: "relative", height: 620 }}
        onMouseOver={handleNameHover}
        onMouseLeave={() => setNameTip(null)}
      >
        <ReactFlow
          nodes={builtNodes}
          edges={flowEdges}
          nodeTypes={ORG_NODE_TYPES}
          edgeTypes={ORG_EDGE_TYPES}
          onConnect={onConnect}
          onConnectEnd={onConnectEnd}
          onEdgeClick={onEdgeClick}
          onPaneClick={() => setEdgeMenu(null)}
          fitView
          fitViewOptions={{ padding: 0.15, maxZoom: 1 }}
          connectionRadius={40}
          nodesDraggable={false}
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={24} size={1} />
        </ReactFlow>
        {nameTip && (
          <div className="orgNameTooltip" style={{ left: nameTip.left, top: nameTip.top }}>
            {nameTip.text}
          </div>
        )}
        {edgeMenu && (
          <div className="gcAddPopover" style={{ left: edgeMenu.left, top: edgeMenu.top }} role="menu">
            {edgeMenu.kind === "dotted" && (
              <button
                type="button"
                className="gcAddOption"
                onClick={() => { props.onMakePrimary(edgeMenu.edgeId); setEdgeMenu(null); }}
              >
                Make primary
              </button>
            )}
            {edgeMenu.kind === "solid" && (
              <button
                type="button"
                className="gcAddOption"
                onClick={() => { props.onMakeDotted(edgeMenu.edgeId); setEdgeMenu(null); }}
              >
                Make dotted
              </button>
            )}
            <button
              type="button"
              className="gcAddOption"
              onClick={() => { props.onRemoveEdge(edgeMenu.edgeId); setEdgeMenu(null); }}
            >
              Remove
            </button>
            <button type="button" className="gcAddOptionCancel" onClick={() => setEdgeMenu(null)}>
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


